<template>
  <v-container fluid class="pa-6 monitor-shell" style="background:#f4f6f9;min-height:100vh">

    <transition name="monitor-intro">
      <div v-if="showIntroSplash" class="monitor-intro-overlay">
        <div class="monitor-intro-burst"></div>
        <div class="monitor-intro-ring monitor-intro-ring--outer"></div>
        <div class="monitor-intro-ring monitor-intro-ring--inner"></div>
        <div class="monitor-intro-icon-wrap">
          <v-avatar color="#0f766e" size="88" class="monitor-intro-icon" rounded>
            <v-icon dark size="46">mdi-radar</v-icon>
          </v-avatar>
        </div>
      </div>
    </transition>

    <!-- Header -->
    <v-row class="mb-4">
      <v-col cols="12">
        <v-card elevation="0" outlined class="monitor-header-card">
          <v-card-text class="monitor-header py-4 px-6 d-flex align-center">
            <v-avatar color="#0f766e" size="54" class="mr-4 monitor-header__avatar" rounded>
              <v-icon dark>mdi-radar</v-icon>
            </v-avatar>
            <div class="flex-grow-1 monitor-header__copy">
              <transition name="header-swap" mode="out-in">
                <div :key="`header-${step}`" class="monitor-header__text">
                  <div class="monitor-header__kicker">
                    <span class="monitor-header__kicker-line"></span>
                    <span>{{ $t('Reports.RealtimeSensors.header.kicker') }}</span>
                  </div>
                  <div class="monitor-header__title mb-0">
                    <span class="monitor-header__title-main">{{ $t('Reports.RealtimeSensors.header.titleMain') }}</span>
                    <span class="monitor-header__title-ghost">{{ $t('Reports.RealtimeSensors.header.titleGhost') }}</span>
                  </div>
                  <div class="monitor-header__title-accent"></div>
                  <div class="monitor-header__subtitle text-caption text--secondary">
                    {{ headerSub }}
                  </div>
                </div>
              </transition>
            </div>
            <div class="monitor-header__aside">
              <v-tooltip bottom color="blue-grey darken-4">
                <template v-slot:activator="{ on, attrs }">
                  <div v-bind="attrs" class="str-language-control" v-on="on">
                    <v-icon small color="primary" class="mr-2">mdi-translate</v-icon>
                    <v-btn-toggle :value="currentLocale" mandatory dense class="str-language-toggle" @change="setLocale">
                      <v-btn small value="es" class="str-language-btn"
                        :class="{ 'str-language-btn--active': currentLocale === 'es' }"
                        :aria-label="$t('Reports.RealtimeSensors.language.spanish')">
                        ES
                      </v-btn>
                      <v-btn small value="en" class="str-language-btn"
                        :class="{ 'str-language-btn--active': currentLocale === 'en' }"
                        :aria-label="$t('Reports.RealtimeSensors.language.english')">
                        EN
                      </v-btn>
                    </v-btn-toggle>
                  </div>
                </template>
                <span class="white--text">{{ $t('Reports.RealtimeSensors.language.tooltip') }}</span>
              </v-tooltip>
              <div class="monitor-header__aside-chips">
                <v-chip v-if="monitoring" color="success" small class="mr-2 monitor-status-chip">
                  <v-icon x-small left>mdi-circle</v-icon>
                  {{ $t('Reports.RealtimeSensors.status.live') }}
                </v-chip>
                <v-chip outlined small class="monitor-step-chip">{{ $t('Reports.RealtimeSensors.steps.stepOf', { step }) }}</v-chip>
              </div>
            </div>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>

    <!-- Stepper -->
    <v-row class="mb-5">
      <v-col cols="12">
        <v-card
          elevation="0"
          outlined
          class="monitor-steps-card"
          :class="{ 'monitor-steps-card--complete': step === 3 }"
        >
          <v-card-text class="pa-3 pa-md-4">
            <v-row no-gutters align="center" class="monitor-steps-row">
              <v-col v-for="(s, i) in steps" :key="i" class="d-flex align-center">
                <v-btn
                  :color="isCompletedFlow(i + 1) ? '#0f766e' : (step === i+1 ? '#0f766e' : stepDone(i+1) ? '#0f766e' : 'grey lighten-2')"
                  :text="!isCompletedFlow(i + 1) && !stepDone(i+1) && step !== i+1"
                  :outlined="!isCompletedFlow(i + 1) && stepDone(i+1) && step !== i+1"
                  rounded
                  class="flex-grow-1 monitor-step-btn"
                  :class="{
                    [`monitor-step-btn--state-${stepState(i + 1)}`]: true,
                    'monitor-step-btn--complete': isCompletedFlow(i + 1),
                  }"
                  :style="stepCardStyle(i + 1)"
                  :disabled="!canGoStep(i+1)"
                  @click="goStep(i+1)"
                >
                  <div class="monitor-step-card-top">
                  </div>
                  <v-avatar
                    :color="step === i+1 ? 'white' : stepDone(i+1) ? 'white' : 'grey lighten-1'"
                    size="34"
                    class="mr-3 monitor-step-avatar"
                    :class="{
                      [`monitor-step-avatar--state-${stepState(i + 1)}`]: true,
                      'monitor-step-avatar--complete': isCompletedFlow(i + 1),
                    }"
                    style="font-size:14px;font-weight:800"
                  >
                    <v-icon class="monitor-step-avatar-icon" :style="stepIconStyle(i + 1)">{{ stepIconForIndex(i + 1) }}</v-icon>
                    <span :style="`color:${step===i+1?'#1976d2':stepDone(i+1)?'#1976d2':'#9e9e9e'}`">
                      {{ stepDone(i+1) && step !== i+1 ? '✓' : i+1 }}
                    </span>
                  </v-avatar>
                  <div class="text-left monitor-step-copy" style="line-height:1.2">
                    <div class="d-flex align-center monitor-step-label" style="font-size:15px;font-weight:700">
                      <v-icon class="mr-2 monitor-step-label-icon" :style="stepIconStyle(i + 1)">{{ stepIconForIndex(i + 1) }}</v-icon>
                      {{ s.label }}
                    </div>
                    <div class="monitor-step-number" :style="stepNumberStyle(i + 1)">
                      {{ i + 1 }}
                    </div>
                    <div class="monitor-step-value" style="font-size:12px;max-width:100%">
                      {{ s.value || $t('Reports.RealtimeSensors.steps.select') }}
                    </div>
                  </div>
                </v-btn>
                <v-icon
                  v-if="i < 2"
                  color="grey lighten-1"
                  class="mx-2 monitor-step-chevron"
                  :class="{ 'monitor-step-chevron--pulse': step < 3 }"
                >
                  mdi-chevron-right
                </v-icon>
              </v-col>
            </v-row>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>

    <!-- STEP 1: Áreas -->
    <transition name="fade-slide">
      <div v-if="step === 1" key="step1">
        <div class="section-banner section-banner--blue mb-4">
          <div class="section-banner__icon">
            <v-icon color="primary" size="22">mdi-domain</v-icon>
          </div>
          <span class="text-subtitle-2 font-weight-bold">{{ $t('Reports.RealtimeSensors.banners.selectArea') }}</span>
          <v-chip x-small outlined class="section-banner__chip">{{ areas.length }} {{ $t('Reports.RealtimeSensors.banners.available') }}</v-chip>
        </div>
        <v-row>
          <v-col v-for="(area, areaIdx) in areas" :key="area.idArea" cols="12" sm="6" md="4">
            <v-card
              outlined
              class="area-card premium-card premium-card--area"
              :class="{ 'selected-card': selectedArea && selectedArea.idArea === area.idArea }"
              :style="`animation-delay:${areaIdx * 70}ms`"
              @click="selectArea(area)"
              hover
            >
              <div class="premium-card__glow" :style="`background:linear-gradient(135deg, ${hexFromColor(area.color, 0.22)} 0%, rgba(255,255,255,0) 72%)`"></div>
              <v-card-text class="pa-4">
                <div class="d-flex align-center mb-2">
                  <v-avatar :color="area.color" size="44" rounded class="mr-3">
                    <v-icon dark>{{ area.icon }}</v-icon>
                  </v-avatar>
                  <div class="flex-grow-1">
                    <div class="text-subtitle-2 font-weight-bold">{{ area.nombre }}</div>
                    <div class="text-caption text--secondary">{{ area.descripcion }}</div>
                  </div>
                  <v-icon v-if="selectedArea && selectedArea.idArea === area.idArea" color="primary">
                    mdi-check-circle
                  </v-icon>
                </div>
                <v-divider class="my-2"/>
                <div class="d-flex align-center justify-space-between">
                  <v-chip x-small :color="area.statusColor" dark>{{ $t('Reports.RealtimeSensors.area.active') }}</v-chip>
                  <span class="text-caption text--secondary">
                    {{ area.maquinas }} {{ $t('Reports.RealtimeSensors.banners.machinesUnit') }}
                  </span>
                </div>
              </v-card-text>
            </v-card>
          </v-col>
        </v-row>
      </div>
    </transition>

    <!-- STEP 2: Máquinas -->
    <transition name="fade-slide">
      <div v-if="step === 2" key="step2">
        <div class="section-banner section-banner--slate mb-4">
          <v-btn icon small class="mr-2" @click="goStep(1)">
            <v-icon small>mdi-arrow-left</v-icon>
          </v-btn>
          <div class="section-banner__icon">
            <v-icon color="primary" size="22">mdi-robot-industrial</v-icon>
          </div>
          <span class="text-subtitle-2 font-weight-bold section-banner__title">
            {{ selectedArea ? selectedArea.nombre : '' }} — {{ $t('Reports.RealtimeSensors.banners.machines') }}
          </span>
          <v-chip x-small outlined class="section-banner__chip">{{ maquinas.length }} {{ $t('Reports.RealtimeSensors.banners.available') }}</v-chip>
          <v-progress-circular v-if="loadingMaquinas" indeterminate color="primary" size="20" class="ml-3"/>
        </div>
        <v-row>
          <v-col v-for="(maq, maqIdx) in maquinas" :key="maq.idMaquinaIns" cols="12" sm="6" md="4">
            <v-card
              outlined
              class="area-card premium-card premium-card--machine"
              :class="{ 'selected-card': selectedMaquina && selectedMaquina.idMaquinaIns === maq.idMaquinaIns }"
              :style="`animation-delay:${maqIdx * 70}ms`"
              @click="selectMaquina(maq)"
              hover
            >
              <div class="premium-card__glow" :style="`background:linear-gradient(135deg, ${hexFromStatus(maq.estado, 0.2)} 0%, rgba(255,255,255,0) 70%)`"></div>
              <v-card-text class="pa-4">
                <div class="d-flex align-center mb-2">
                  <v-avatar :color="getStatusColor(maq.estado)" size="44" rounded class="mr-3">
                    <v-icon dark>{{ getMachineIcon(maq) }}</v-icon>
                  </v-avatar>
                  <div class="flex-grow-1">
                    <div class="text-subtitle-2 font-weight-bold">{{ maq.nombre }}</div>
                    <div class="text-caption text--secondary">{{ maq.marca }} · {{ maq.modelo }}</div>
                  </div>
                  <v-icon v-if="selectedMaquina && selectedMaquina.idMaquinaIns === maq.idMaquinaIns" color="primary">
                    mdi-check-circle
                  </v-icon>
                </div>
                <v-divider class="my-2"/>
                <div class="d-flex align-center justify-space-between">
                  <v-chip x-small :color="getStatusColor(maq.estado)" dark>{{ maq.estado || $t('Reports.RealtimeSensors.state.noStatus') }}</v-chip>
                  <span class="text-caption text--secondary">
                    {{ maq.sensores }} sensores
                  </span>
                </div>
              </v-card-text>
            </v-card>
          </v-col>
          <v-col v-if="!loadingMaquinas && maquinas.length === 0" cols="12">
            <v-card outlined class="text-center pa-8">
              <v-icon size="48" color="grey lighten-1">mdi-cog-off</v-icon>
              <div class="text-body-2 text--secondary mt-2">{{ $t('Reports.RealtimeSensors.empty.noMachines') }}</div>
            </v-card>
          </v-col>
        </v-row>
      </div>
    </transition>

    <!-- STEP 3: Sensores + Gráfica -->
    <transition name="fade-slide">
      <div v-if="step === 3" key="step3">
        <div class="section-banner section-banner--violet mb-4">
          <v-btn icon small class="mr-2" @click="goStep(2)">
            <v-icon small>mdi-arrow-left</v-icon>
          </v-btn>
          <div class="section-banner__icon">
            <v-icon color="primary" size="22">mdi-radar</v-icon>
          </div>
          <span class="text-subtitle-2 font-weight-bold section-banner__title">
            {{ selectedMaquina ? selectedMaquina.nombre : '' }} — {{ $t('Reports.RealtimeSensors.banners.sensors') }}
          </span>
          <v-chip x-small outlined class="section-banner__chip">
            {{ $t('Reports.RealtimeSensors.banners.sensorsVariables', { sensors: sensorGroups.length, variables: sensores.length }) }}
          </v-chip>
          <v-progress-circular v-if="loadingSensores" indeterminate color="primary" size="20" class="ml-3"/>
        </div>

        <!-- Sensor Cards -->
        <v-row class="mb-4">
          <v-col
            v-for="(sensorGroup, groupIdx) in sensorGroups"
            :key="`sensor-group-${sensorGroup.idSensor}-${sensorGroup.sensorCodigo}`"
            cols="12"
          >
            <v-card
              outlined
              class="sensor-group-card premium-card"
              :style="`animation-delay:${groupIdx * 70}ms;--sensor-accent:${sensorGroup.color};--sensor-soft:${sensorGroup.colorBg}`"
            >
              <div class="premium-card__glow" :style="`background:linear-gradient(135deg, ${alphaColor(sensorGroup.color, 0.14)} 0%, rgba(255,255,255,0) 72%)`"></div>
              <v-card-text class="pa-0">
                <div class="sensor-group-card__header">
                  <div class="sensor-group-card__main">
                    <div class="sensor-group-card__icon-box">
                      <v-icon :color="sensorGroup.color" size="26">{{ sensorGroup.icon }}</v-icon>
                    </div>
                    <div class="sensor-group-card__copy">
                      <div class="sensor-group-card__eyebrow">{{ $t('Reports.RealtimeSensors.card.sensor') }}</div>
                      <div class="sensor-group-card__title">{{ sensorGroup.sensorNombre }}</div>
                      <div class="sensor-group-card__subtitle">
                        {{ sensorGroup.sensorCodigo || $t('Reports.RealtimeSensors.card.noCode') }}
                        <span class="sensor-card__subtitle-dot">-</span>
                        {{ sensorGroup.variables.length }} {{ $t('Reports.RealtimeSensors.card.variablesLabel') }}
                      </div>
                    </div>
                  </div>
                  <v-chip small outlined class="sensor-group-card__chip">
                    {{ sensorGroup.variables.length }} {{ sensorGroup.variables.length === 1 ? 'variable' : 'variables' }}
                  </v-chip>
                </div>

                <v-row dense class="sensor-group-card__variables">
                  <v-col
                    v-for="(sensor, idx) in sensorGroup.variables"
                    :key="sensor.token"
                    cols="12" sm="6" md="4" lg="3"
                  >
                    <v-card
                      outlined
                      class="sensor-card premium-card premium-card--sensor"
                      :class="{
                        'selected-card': selectedSensor && selectedSensor.token === sensor.token,
                        'sensor-card--alert': sensorOutOfRange(sensor),
                      }"
                      :style="`animation-delay:${(groupIdx * 90) + (idx * 45)}ms;--sensor-accent:${sensor.color};--sensor-soft:${sensor.colorBg}`"
                      @click="selectSensor(sensor)"
                      hover
                    >
                      <div class="premium-card__glow" :style="`background:linear-gradient(135deg, ${alphaColor(sensor.color, 0.18)} 0%, rgba(255,255,255,0) 72%)`"></div>
                      <v-card-text class="pa-0 sensor-card__body">
                        <div class="sensor-card__shell">
                  <div class="sensor-card__header">
                    <div class="sensor-card__header-main">
                      <div class="sensor-card__icon-box">
                        <v-icon :color="sensor.color" size="24">{{ sensor.icon }}</v-icon>
                      </div>
                      <div class="sensor-card__copy">
                        <div class="sensor-card__title">{{ sensor.nombre }}</div>
                        <div class="sensor-card__subtitle">
                          {{ (sensor.nombreTipoSensor || sensor.descripcion || sensor.tag || 'Sensor').toUpperCase() }}
                          <span class="sensor-card__subtitle-dot">•</span>
                          {{ sensor.token }}
                        </div>
                      </div>
                    </div>
                  </div>

                  <div class="sensor-card__section">
                    <div class="sensor-card__section-label">{{ $t('Reports.RealtimeSensors.card.lastReading') }}</div>
                    <div class="sensor-card__reading-row">
                      <span class="sensor-card__reading-value">{{ sensorCurrentValue(sensor).toFixed(2) }}</span>
                      <span class="sensor-card__reading-unit">{{ sensor.unidad }}</span>
                    </div>

                    <div class="sensor-card__bar">
                      <div class="sensor-card__bar-track"></div>
                      <div
                        class="sensor-card__bar-fill"
                        :style="`width:${sensorProgressPercent(sensor)}%`"
                      ></div>
                      <div
                        class="sensor-card__bar-knob"
                        :style="`left:calc(${sensorProgressPercent(sensor)}% - 10px)`"
                      ></div>
                    </div>

                    <div class="sensor-card__bar-meta">
                      <span>{{ sensorRangeMin(sensor).toFixed(0) }} {{ sensor.unidad }}</span>
                      <span>Normal: {{ sensorRangeMin(sensor).toFixed(0) }}–{{ sensorRangeMax(sensor).toFixed(0) }}</span>
                      <span :style="`color:${sensor.color}`">{{ sensorCurrentValue(sensor).toFixed(1) }}</span>
                    </div>

                    <!-- Configuracion de rangos (barra de zonas) -->
                    <div v-if="sensorRangoBar(sensor)" class="sensor-rango-config">
                      <div class="sensor-rango-config__head">
                        <v-icon small>mdi-chart-bell-curve</v-icon>
                        {{ $t('Reports.RealtimeSensors.rango.config') }}
                      </div>
                      <div class="sensor-rango-bar">
                        <div
                          v-for="(seg, i) in sensorRangoBar(sensor).segments"
                          :key="'srseg-' + i"
                          class="sensor-rango-bar__seg"
                          :class="'sensor-rango-bar__seg--' + seg.kind"
                          :style="{ flex: seg.flex }"
                        ></div>
                      </div>
                      <div class="sensor-rango-bar-axis">
                        <span
                          v-for="(tick, i) in sensorRangoBar(sensor).ticks"
                          :key="'srtick-' + i"
                          class="sensor-rango-bar-axis__tick"
                          :style="{ left: tick.pos + '%' }"
                        >{{ tick.value.toFixed(2) }}</span>
                      </div>
                      <div class="sensor-rango-config__legend">
                        <span class="sensor-rango-config__legend-item sensor-rango-config__legend-item--alarma">{{ $t('Reports.RealtimeSensors.legend.alarm') }}</span>
                        <span class="sensor-rango-config__legend-item sensor-rango-config__legend-item--adv">{{ $t('Reports.RealtimeSensors.legend.warning') }}</span>
                        <span class="sensor-rango-config__legend-item sensor-rango-config__legend-item--normal">{{ $t('Reports.RealtimeSensors.legend.normal') }}</span>
                      </div>
                    </div>
                  </div>

                  <div class="sensor-card__section sensor-card__section--meta">
                    <div class="sensor-card__meta-item">
                      <span class="sensor-card__meta-label">Tendencia</span>
                      <span class="sensor-card__state" :style="`color:${getTrend(sensor).color}`">
                        {{ getTrend(sensor).value }}
                      </span>
                    </div>
                  </div>

                  <div class="sensor-card__footer">
                    <span class="sensor-card__timestamp">{{ sensor.fechaValorActual || $t('Reports.RealtimeSensors.state.noRecentReading') }}</span>
                    <v-chip
                      x-small
                              :color="sensorOutOfRange(sensor) ? 'red lighten-5' : sensor.tagColor"
                              :dark="!sensorOutOfRange(sensor)"
                              :text-color="sensorOutOfRange(sensor) ? '#dc2626' : undefined"
                              :class="['sensor-card__chip', { 'sensor-card__chip--alert': sensorOutOfRange(sensor) }]"
                            >
                              {{ sensorOutOfRange(sensor) ? $t('Reports.RealtimeSensors.state.outOfRange') : sensorTagLabel(sensor) }}
                            </v-chip>
                  </div>
                        </div>
                      </v-card-text>
                    </v-card>
                  </v-col>

                  <v-col v-if="sensorGroup.variables.length === 0" cols="12">
                    <div class="sensor-group-card__empty">
                      <v-icon color="grey lighten-1" class="mr-2">mdi-variable-box</v-icon>
                      {{ $t('Reports.RealtimeSensors.card.noVariables') }}
                    </div>
                  </v-col>
                </v-row>
              </v-card-text>
            </v-card>
          </v-col>

          <v-col v-if="!loadingSensores && sensorGroups.length === 0" cols="12">
            <v-card outlined class="text-center pa-8">
              <v-icon size="48" color="grey lighten-1">mdi-sine-wave</v-icon>
              <div class="text-body-2 text--secondary mt-2">{{ $t('Reports.RealtimeSensors.empty.noSensors') }}</div>
            </v-card>
          </v-col>
        </v-row>

        <!-- Monitor Panel -->
        <transition name="fade-slide">
          <v-card v-if="selectedSensor" outlined class="mt-2 monitor-panel-card" key="monitor">
            <v-card-text class="pa-0">
              <div class="monitor-panel-hero" :style="chartAccentStyle">
                <div class="monitor-panel-hero__glow"></div>
                <div class="monitor-panel-hero__content">
                  <div class="monitor-summary-card">
                    <div class="monitor-summary-card__main">
                      <div class="monitor-summary-card__avatar" :style="`--selected-accent:${selectedSensor.color};--selected-soft:${selectedSensor.colorBg}`">
                        <v-icon :color="selectedSensor.color" size="30">{{ selectedSensor.icon || 'mdi-pulse' }}</v-icon>
                        <span class="monitor-summary-card__avatar-pulse"></span>
                      </div>
                      <div class="monitor-summary-card__copy">
                        <div class="monitor-summary-card__eyebrow">
                          <v-icon x-small>mdi-creation</v-icon>
                          {{ $t('Reports.RealtimeSensors.panel.controlCenterActive') }}
                        </div>
                        <div class="monitor-summary-card__title">{{ selectedSensor.nombre }}</div>
                        <div class="monitor-summary-card__meta">
                          <span>{{ (selectedSensor.sensorNombre || selectedSensor.tag || selectedSensor.nombreTipoSensor || 'Sensor').toUpperCase() }}</span>
                          <span class="monitor-summary-card__dot">•</span>
                          <span>{{ selectedSensor.token }}</span>
                        </div>
                      </div>
                    </div>

                    <div class="monitor-summary-card__actions">
                      <div class="monitor-live-pill">
                        <span class="monitor-live-pill__dot"></span>
                        {{ monitoring ? $t('Reports.RealtimeSensors.status.live') : $t('Reports.RealtimeSensors.status.paused') }}
                      </div>
                    </div>
                  </div>

                  <v-row dense class="monitor-overview-grid">
                    <v-col cols="12" md="4">
                      <div class="monitor-highlight-card monitor-highlight-card--current">
                        <div class="monitor-highlight-card__top">
                          <div class="monitor-highlight-card__icon"><v-icon>mdi-speedometer</v-icon></div>
                          <div class="monitor-highlight-card__label">{{ $t('Reports.RealtimeSensors.panel.currentValue') }}</div>
                        </div>
                        <div class="monitor-highlight-card__value" :style="`color:${selectedSensor.color}`">
                          {{ selectedSensorCurrentValue.toFixed(2) }}
                          <small>{{ selectedSensor.unidad }}</small>
                        </div>
                        <div class="monitor-highlight-card__meta">
                          {{ panelStatusLabel }}
                        </div>
                      </div>
                    </v-col>
                    <v-col cols="12" md="4">
                      <div class="monitor-highlight-card monitor-highlight-card--range">
                        <div class="monitor-highlight-card__top">
                          <div class="monitor-highlight-card__icon"><v-icon>mdi-timeline-clock-outline</v-icon></div>
                          <div class="monitor-highlight-card__label">{{ $t('Reports.RealtimeSensors.panel.visibleWindow') }}</div>
                        </div>
                        <div class="monitor-highlight-card__subvalue">{{ formattedRangeLabel }}</div>
                        <div class="monitor-highlight-card__meta">
                          {{ hasChartData ? selectedSensorRangeLabel : $t('Reports.RealtimeSensors.panel.waitingData') }}
                        </div>
                      </div>
                    </v-col>
                    <v-col cols="12" md="4">
                      <div class="monitor-highlight-card monitor-highlight-card--state" :class="{ 'monitor-highlight-card--alert': selectedSensorOutOfRange }">
                        <div class="monitor-highlight-card__top">
                          <div class="monitor-highlight-card__icon">
                            <v-icon>{{ selectedSensorOutOfRange ? 'mdi-alert-circle-outline' : 'mdi-shield-check-outline' }}</v-icon>
                          </div>
                          <div class="monitor-highlight-card__label">{{ $t('Reports.RealtimeSensors.panel.sensorState') }}</div>
                        </div>
                        <div class="monitor-highlight-card__subvalue" :style="selectedSensorStatusStyle">
                          {{ selectedSensorStatusLabel }}
                        </div>
                        <div class="monitor-highlight-card__meta">
                          {{ selectedSensorStatusMeta }}
                        </div>
                      </div>
                    </v-col>
                  </v-row>
                </div>
              </div>

              <div class="monitor-panel-body pa-4 pa-md-5">
                <div class="monitor-controls-wrap">
                  <div class="monitor-controls-heading">
                    <div class="monitor-controls-heading__icon"><v-icon>mdi-tune-vertical-variant</v-icon></div>
                    <div>
                      <div class="monitor-controls-label">{{ $t('Reports.RealtimeSensors.panel.exploreHistory') }}</div>
                      <div class="monitor-controls-help">{{ $t('Reports.RealtimeSensors.panel.exploreHelp') }}</div>
                    </div>
                  </div>
                  <div class="monitor-controls-track">
                    <button
                      v-for="v in ventanas"
                      :key="v.value"
                      type="button"
                      class="monitor-window-pill"
                      :class="{ 'monitor-window-pill--active': ventanaSegundos === v.value }"
                      @click="setVentana(v.value)"
                    >
                      {{ v.label }}
                    </button>
                  </div>
                  <v-btn
                    :color="monitoring ? 'error' : 'success'"
                    rounded
                    class="monitor-controls-trigger"
                    @click="toggleMonitoreo"
                  >
                    <v-icon left>{{ monitoring ? 'mdi-pause-circle-outline' : 'mdi-play-circle-outline' }}</v-icon>
                    {{ monitoring ? $t('Reports.RealtimeSensors.panel.pause') : $t('Reports.RealtimeSensors.panel.start') }}
                  </v-btn>
                </div>

                <div class="monitor-chart-shell">
                  <div class="monitor-chart-shell__header">
                    <div class="monitor-chart-shell__heading">
                      <div class="monitor-chart-shell__icon"><v-icon>mdi-chart-timeline-variant-shimmer</v-icon></div>
                      <div>
                      <div class="monitor-chart-shell__eyebrow">{{ $t('Reports.RealtimeSensors.panel.continuousReading') }}</div>
                      <div class="monitor-chart-shell__title">{{ $t('Reports.RealtimeSensors.panel.sensorPulse') }}</div>
                      <div class="monitor-chart-shell__subtitle">
                        {{ $t('Reports.RealtimeSensors.panel.sensorPulseSub') }}
                      </div>
                      </div>
                    </div>
                    <div class="monitor-chart-shell__status">
                      <v-icon :color="monitoring ? 'success' : 'grey'" x-small class="mr-2">
                        mdi-circle
                      </v-icon>
                      <span>{{ panelStatusLabel }}</span>
                    </div>
                  </div>

                  <div class="monitor-chart-shell__body">
                    <div style="position:relative;height:390px">
                      <apexchart
                        v-if="chartMontado"
                        ref="chart"
                        height="390"
                        :options="chartOptions"
                        :series="chartSeries"
                      />
                      <div v-else class="d-flex align-center justify-center monitor-chart-shell__empty">
                        <div class="text-center">
                          <v-progress-circular indeterminate color="primary" size="36"/>
                          <div class="text-caption text--secondary mt-3">{{ $t('Reports.RealtimeSensors.panel.preparing') }}</div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                <!-- Configuracion de rangos (panel premium) -->
                <div v-if="selectedSensorRangoBar" class="rango-monitor mt-4">
                  <div class="rango-monitor__header">
                    <div class="rango-monitor__title-row">
                      <div class="rango-monitor__icon-box">
                        <v-icon>mdi-tune-variant</v-icon>
                      </div>
                      <div>
                        <div class="rango-monitor__eyebrow">{{ $t('Reports.RealtimeSensors.rango.config') }}</div>
                        <div class="rango-monitor__title">{{ $t('Reports.RealtimeSensors.rango.title') }}</div>
                        <div class="rango-monitor__subtitle">
                          {{ $t('Reports.RealtimeSensors.rango.subtitle') }}
                        </div>
                      </div>
                    </div>
                    <div class="rango-monitor__current">
                      <div class="rango-monitor__current-label">{{ $t('Reports.RealtimeSensors.rango.currentValue') }}</div>
                      <div class="rango-monitor__current-value" :style="`color:${selectedSensorRangoZoneColor}`">
                        {{ selectedSensorCurrentValue.toFixed(2) }} {{ selectedSensor.unidad }}
                      </div>
                      <div class="rango-monitor__current-zone" :style="`color:${selectedSensorRangoZoneColor}`">
                        {{ selectedSensorRangoZoneLabel }}
                      </div>
                    </div>
                  </div>

                  <!-- Legend de zonas -->
                  <div class="rango-monitor__legend">
                    <div v-if="selectedSensorRangoBar.alarmaBaja" class="rango-monitor__legend-item rango-monitor__legend-item--alarma">
                      <div class="rango-monitor__legend-title">{{ $t('Reports.RealtimeSensors.rango.alarm') }}</div>
                      <div class="rango-monitor__legend-range">&lt; {{ formatRangoNum(selectedSensorRangoBar.alarmaBaja.upper) }} {{ selectedSensor.unidad }}</div>
                    </div>
                    <div v-if="selectedSensorRangoBar.advBaja" class="rango-monitor__legend-item rango-monitor__legend-item--adv">
                      <div class="rango-monitor__legend-title">{{ $t('Reports.RealtimeSensors.rango.warning') }}</div>
                      <div class="rango-monitor__legend-range">{{ formatRangoNum(selectedSensorRangoBar.advBaja.lower) }} – {{ formatRangoNum(selectedSensorRangoBar.advBaja.upper) }} {{ selectedSensor.unidad }}</div>
                    </div>
                    <div class="rango-monitor__legend-item rango-monitor__legend-item--normal">
                      <div class="rango-monitor__legend-title">{{ $t('Reports.RealtimeSensors.rango.normal') }}</div>
                      <div class="rango-monitor__legend-range">{{ formatRangoNum(selectedSensorRangoBar.normal.lower) }} – {{ formatRangoNum(selectedSensorRangoBar.normal.upper) }} {{ selectedSensor.unidad }}</div>
                    </div>
                    <div v-if="selectedSensorRangoBar.advAlta" class="rango-monitor__legend-item rango-monitor__legend-item--adv">
                      <div class="rango-monitor__legend-title">{{ $t('Reports.RealtimeSensors.rango.warning') }}</div>
                      <div class="rango-monitor__legend-range">{{ formatRangoNum(selectedSensorRangoBar.advAlta.lower) }} – {{ formatRangoNum(selectedSensorRangoBar.advAlta.upper) }} {{ selectedSensor.unidad }}</div>
                    </div>
                    <div v-if="selectedSensorRangoBar.alarmaAlta" class="rango-monitor__legend-item rango-monitor__legend-item--alarma">
                      <div class="rango-monitor__legend-title">{{ $t('Reports.RealtimeSensors.rango.alarm') }}</div>
                      <div class="rango-monitor__legend-range">&gt; {{ formatRangoNum(selectedSensorRangoBar.alarmaAlta.lower) }} {{ selectedSensor.unidad }}</div>
                    </div>
                  </div>

                  <!-- Barra de zonas -->
                  <div class="rango-monitor__bar-wrap">
                    <div class="rango-monitor__bar">
                      <div
                        v-for="(seg, i) in selectedSensorRangoBar.segments"
                        :key="'mseg-' + i"
                        class="rango-monitor__bar-seg"
                        :class="'rango-monitor__bar-seg--' + seg.kind"
                        :style="{ flex: seg.flex }"
                      ></div>
                    </div>
                    <!-- Marker del valor actual -->
                    <div
                      v-if="selectedSensorRangoBarValuePos !== null"
                      class="rango-monitor__marker"
                      :style="{ left: selectedSensorRangoBarValuePos + '%' }"
                    >
                      <div class="rango-monitor__marker-pin" :style="`background:${selectedSensorRangoZoneColor}`"></div>
                      <div class="rango-monitor__marker-value" :style="`background:${selectedSensorRangoZoneColor}`">
                        {{ selectedSensorCurrentValue.toFixed(2) }} {{ selectedSensor.unidad }}
                      </div>
                    </div>
                  </div>

                  <!-- Eje con ticks -->
                  <div class="rango-monitor__axis">
                    <span
                      v-for="(tick, i) in selectedSensorRangoBar.ticks"
                      :key="'maxis-' + i"
                      class="rango-monitor__axis-tick"
                      :style="{ left: tick.pos + '%' }"
                    >{{ formatRangoNum(tick.value) }} {{ selectedSensor.unidad }}</span>
                  </div>

                  <!-- Resumen de configuracion -->
                  <div class="rango-monitor__summary">
                    <div v-if="selectedSensorRangoBar.alarmaBaja" class="rango-monitor__summary-cell rango-monitor__summary-cell--alarma">
                      <div class="rango-monitor__summary-label">{{ $t('Reports.RealtimeSensors.rango.alarmLow') }}</div>
                      <div class="rango-monitor__summary-sub">{{ $t('Reports.RealtimeSensors.rango.lessThan') }}</div>
                      <div class="rango-monitor__summary-value">{{ formatRangoNum(selectedSensorRangoBar.alarmaBaja.upper) }} {{ selectedSensor.unidad }}</div>
                    </div>
                    <div v-if="selectedSensorRangoBar.advBaja" class="rango-monitor__summary-cell rango-monitor__summary-cell--adv">
                      <div class="rango-monitor__summary-label">{{ $t('Reports.RealtimeSensors.rango.warningLower') }}</div>
                      <div class="rango-monitor__summary-sub">{{ $t('Reports.RealtimeSensors.rango.between') }}</div>
                      <div class="rango-monitor__summary-value">{{ formatRangoNum(selectedSensorRangoBar.advBaja.lower) }} — {{ formatRangoNum(selectedSensorRangoBar.advBaja.upper) }} {{ selectedSensor.unidad }}</div>
                    </div>
                    <div class="rango-monitor__summary-cell rango-monitor__summary-cell--normal">
                      <div class="rango-monitor__summary-label">{{ $t('Reports.RealtimeSensors.rango.normalOperation') }}</div>
                      <div class="rango-monitor__summary-sub">{{ $t('Reports.RealtimeSensors.rango.between') }}</div>
                      <div class="rango-monitor__summary-value">{{ formatRangoNum(selectedSensorRangoBar.normal.lower) }} — {{ formatRangoNum(selectedSensorRangoBar.normal.upper) }} {{ selectedSensor.unidad }}</div>
                    </div>
                    <div v-if="selectedSensorRangoBar.advAlta" class="rango-monitor__summary-cell rango-monitor__summary-cell--adv">
                      <div class="rango-monitor__summary-label">{{ $t('Reports.RealtimeSensors.rango.warningUpper') }}</div>
                      <div class="rango-monitor__summary-sub">{{ $t('Reports.RealtimeSensors.rango.between') }}</div>
                      <div class="rango-monitor__summary-value">{{ formatRangoNum(selectedSensorRangoBar.advAlta.lower) }} — {{ formatRangoNum(selectedSensorRangoBar.advAlta.upper) }} {{ selectedSensor.unidad }}</div>
                    </div>
                    <div v-if="selectedSensorRangoBar.alarmaAlta" class="rango-monitor__summary-cell rango-monitor__summary-cell--alarma">
                      <div class="rango-monitor__summary-label">{{ $t('Reports.RealtimeSensors.rango.alarmHigh') }}</div>
                      <div class="rango-monitor__summary-sub">{{ $t('Reports.RealtimeSensors.rango.greaterThan') }}</div>
                      <div class="rango-monitor__summary-value">{{ formatRangoNum(selectedSensorRangoBar.alarmaAlta.lower) }} {{ selectedSensor.unidad }}</div>
                    </div>
                  </div>
                </div>

                <v-row class="mt-4" dense>
                  <v-col v-for="stat in statsCards" :key="stat.label" cols="12" sm="6" md="4" lg="2">
                    <v-card outlined class="monitor-stat-card pa-4" :style="stat.cardStyle">
                      <div class="monitor-stat-card__head">
                        <div class="monitor-stat-card__icon-wrap" :style="stat.iconWrapStyle">
                          <v-icon :color="stat.iconColor" size="21">{{ stat.icon }}</v-icon>
                        </div>
                        <span class="monitor-stat-card__spark"></span>
                      </div>
                      <div class="monitor-stat-card__copy">
                        <div class="monitor-stat-card__label">
                          {{ stat.label }}
                        </div>
                        <div
                          class="monitor-stat-card__value"
                          :style="stat.color ? `color:${stat.color}` : ''"
                        >
                          {{ stat.value }}
                        </div>
                        <div v-if="stat.meta" class="monitor-stat-card__meta">
                          {{ stat.meta }}
                        </div>
                      </div>
                      <div
                        v-if="stat.badge"
                        class="monitor-stat-card__badge"
                        :style="stat.badgeStyle"
                      >
                        {{ stat.badge }}
                      </div>
                    </v-card>
                  </v-col>
                </v-row>

                <div class="monitor-info-dock mt-4">
                  <div class="monitor-info-dock__title">
                    <v-icon small>mdi-information-slab-circle-outline</v-icon>
                    {{ $t('Reports.RealtimeSensors.info.title') }}
                  </div>
                  <div class="d-flex flex-wrap align-center">
                  <v-chip v-if="selectedSensor.fechaValorActual" small outlined class="mr-2 mb-2 monitor-info-chip">
                    <v-icon left x-small>mdi-clock-outline</v-icon>
                    {{ $t('Reports.RealtimeSensors.info.lastReading') }} {{ selectedSensor.fechaValorActual }}
                  </v-chip>
                  <v-chip v-if="selectedSensor.nombreMaquina" small outlined class="mr-2 mb-2 monitor-info-chip">
                    <v-icon left x-small>mdi-robot-industrial-outline</v-icon>
                    {{ $t('Reports.RealtimeSensors.info.machine') }} {{ selectedSensor.nombreMaquina }}
                  </v-chip>
                  <v-chip v-if="selectedSensor.sensorNombre" small outlined class="mr-2 mb-2 monitor-info-chip">
                    <v-icon left x-small>mdi-access-point</v-icon>
                    {{ $t('Reports.RealtimeSensors.info.sensor') }} {{ selectedSensor.sensorNombre }}
                  </v-chip>
                  <v-chip
                    v-for="rango in selectedSensor.rangos || []"
                    :key="`rango-${selectedSensor.idSensor}-${rango.idRango || rango.IdRango || rango.cantidadMuestras}`"
                    small
                    outlined
                    class="mr-2 mb-2 monitor-info-chip"
                  >
                    {{ rango.cantidadMuestras || rango.CantidadMuestras }} {{ $t('Reports.RealtimeSensors.info.samples') }}
                    {{ rango.desviacionInferior || rango.DesviacionInferior }} {{ $t('Reports.RealtimeSensors.info.to') }}
                    {{ rango.desviacionSuperior || rango.DesviacionSuperior }}
                  </v-chip>
                  <v-chip v-if="selectedSensor.fueraDeRango" small color="red darken-2" dark class="mb-2">
                    {{ $t('Reports.RealtimeSensors.state.currentOutOfRange') }}
                  </v-chip>
                  </div>
                </div>
              </div>
            </v-card-text>
          </v-card>
        </transition>
      </div>
    </transition>

  </v-container>
</template>

<script>
import axios from 'axios'
import moment from 'moment'
import Vue from 'vue'

export default {
  name: 'MonitorSensoresRealtime',

  data() {
    return {
      step: 1,
      selectedArea: null,
      selectedMaquina: null,
      selectedSensor: null,
      monitoring: false,

      // Data
      areas: [],
      maquinas: [],
      sensorGroups: [],
      sensores: [],
      loadingMaquinas: false,
      loadingSensores: false,

      // Chart & live
      chartMontado: false,
      chartSeries: [{ name: '', data: [] }],
      liveValues: {},       // { token: valor }
      sensorHistorial: {},  // { token: [{x, y}] }
      sensorCountByMachine: {},
      intervaloPolling: null,
      pollingInFlight: false,
      pollingGeneration: 0,
      pollingAbortController: null,
      initialDataAbortController: null,
      ventanaSegundos: 300,
      ventanaIdx: 300,
      ventanas: [
        { label: '30s',  value: 30 },
        { label: '1min', value: 60 },
        { label: '5min', value: 300 },
        { label: '15min', value: 900 },
      ],
      maxPuntos: 200,
      ultimaActualizacion: '—',
      showIntroSplash: false,
      introSplashTimeout: null,

      // Estadísticas calculadas
      estadisticas: {
        actual: 0, promedio: 0, mediana: 0,
        minimo: 0, maximo: 0, muestras: 0,
      },
      machineRealtime: null,
    }
  },

  computed: {
    currentLocale() {
      return this.$i18n.locale
    },
    headerSub() {
      if (this.step === 1) return this.$t('Reports.RealtimeSensors.header.subArea')
      if (this.step === 2) return this.$t('Reports.RealtimeSensors.header.subMachine', { area: this.selectedArea?.nombre })
      return this.$t('Reports.RealtimeSensors.header.subMonitor', { machine: this.selectedMaquina?.nombre })
    },

    steps() {
      return [
        { label: this.$t('Reports.RealtimeSensors.steps.area'),    value: this.selectedArea?.nombre },
        { label: this.$t('Reports.RealtimeSensors.steps.machine'), value: this.selectedMaquina?.nombre },
        { label: this.$t('Reports.RealtimeSensors.steps.sensors'), value: this.selectedSensor?.nombre },
      ]
    },

    stats() {
      const e = this.estadisticas
      const u = this.selectedSensor?.unidad || ''
      const trend = this.selectedSensor ? this.getTrend(this.selectedSensor) : { icon: 'mdi-minus', color: '#94a3b8', value: '—' }
      const card = (accent, soft) => ({
        background: `linear-gradient(180deg, rgba(255,255,255,0.98) 0%, ${soft} 100%)`,
        borderColor: this.alphaColor(accent, 0.22),
      })
      const iconWrap = (accent, soft) => ({
        background: `linear-gradient(135deg, ${soft} 0%, rgba(255,255,255,0.96) 100%)`,
        boxShadow: `inset 0 1px 0 rgba(255,255,255,0.86), 0 10px 20px ${this.alphaColor(accent, 0.12)}`,
      })
      const badge = accent => ({
        background: accent,
        color: '#fff',
      })
      return [
        { label: 'Actual',   value: e.actual.toFixed(2)   + ' ' + u, color: this.selectedSensor?.color },
        { label: 'Promedio', value: e.promedio.toFixed(2)  + ' ' + u },
        { label: 'Mediana',  value: e.mediana.toFixed(2)   + ' ' + u },
        { label: 'Mínimo',   value: e.minimo.toFixed(2)    + ' ' + u },
        { label: 'Máximo',   value: e.maximo.toFixed(2)    + ' ' + u },
        { label: 'Muestras', value: String(e.muestras) },
      ]
    },
    statsCards() {
      const e = this.estadisticas
      const u = this.selectedSensor?.unidad || ''
      const trend = this.selectedSensor ? this.getTrend(this.selectedSensor) : { icon: 'mdi-minus', color: '#94a3b8', value: '—' }
      const card = (accent, soft) => ({
        background: `linear-gradient(180deg, rgba(255,255,255,0.98) 0%, ${soft} 100%)`,
        borderColor: this.alphaColor(accent, 0.22),
      })
      const iconWrap = (accent, soft) => ({
        background: `linear-gradient(135deg, ${soft} 0%, rgba(255,255,255,0.96) 100%)`,
        boxShadow: `inset 0 1px 0 rgba(255,255,255,0.86), 0 10px 20px ${this.alphaColor(accent, 0.12)}`,
      })
      const badge = accent => ({
        background: accent,
        color: '#fff',
      })

      return [
        {
          label: this.$t('Reports.RealtimeSensors.stats.currentValue'),
          value: e.actual.toFixed(2) + ' ' + u,
          color: '#2563eb',
          icon: 'mdi-speedometer',
          iconColor: '#1976d2',
          iconWrapStyle: iconWrap('#1976d2', 'rgba(219,234,254,0.92)'),
          cardStyle: card('#1976d2', 'rgba(239,246,255,0.92)'),
          meta: this.panelStatusLabel,
        },
        {
          label: this.$t('Reports.RealtimeSensors.stats.average'),
          value: e.promedio.toFixed(2) + ' ' + u,
          color: '#2f855a',
          icon: 'mdi-chart-timeline-variant',
          iconColor: '#2f855a',
          iconWrapStyle: iconWrap('#2f855a', 'rgba(220,252,231,0.92)'),
          cardStyle: card('#2f855a', 'rgba(240,253,244,0.92)'),
          meta: this.$t('Reports.RealtimeSensors.stats.samples', { count: e.muestras }),
        },
        {
          label: this.$t('Reports.RealtimeSensors.stats.median'),
          value: e.mediana.toFixed(2) + ' ' + u,
          color: '#0f766e',
          icon: 'mdi-chart-bell-curve-cumulative',
          iconColor: '#0f766e',
          iconWrapStyle: iconWrap('#0f766e', 'rgba(204,251,241,0.9)'),
          cardStyle: card('#0f766e', 'rgba(240,253,250,0.92)'),
          meta: this.formattedRangeLabel,
        },
        {
          label: this.$t('Reports.RealtimeSensors.stats.maximum'),
          value: e.maximo.toFixed(2) + ' ' + u,
          color: '#d97706',
          icon: 'mdi-arrow-up-thin-circle-outline',
          iconColor: '#d97706',
          iconWrapStyle: iconWrap('#d97706', 'rgba(254,243,199,0.92)'),
          cardStyle: card('#d97706', 'rgba(255,251,235,0.92)'),
        },
        {
          label: this.$t('Reports.RealtimeSensors.stats.minimum'),
          value: e.minimo.toFixed(2) + ' ' + u,
          color: '#9333ea',
          icon: 'mdi-arrow-down-thin-circle-outline',
          iconColor: '#9333ea',
          iconWrapStyle: iconWrap('#9333ea', 'rgba(243,232,255,0.92)'),
          cardStyle: card('#9333ea', 'rgba(250,245,255,0.92)'),
        },
        {
          label: this.$t('Reports.RealtimeSensors.stats.trend'),
          value: trend.value === '—'
            ? this.$t('Reports.RealtimeSensors.stats.noChange')
            : (trend.value.startsWith('+')
                ? this.$t('Reports.RealtimeSensors.stats.ascending')
                : this.$t('Reports.RealtimeSensors.stats.descending')),
          color: trend.color,
          icon: trend.icon === 'mdi-minus' ? 'mdi-trending-neutral' : trend.icon,
          iconColor: trend.color,
          iconWrapStyle: iconWrap(trend.color, this.alphaColor(trend.color, 0.14)),
          cardStyle: card(trend.color, this.alphaColor(trend.color, 0.06)),
          badge: trend.value === '—' ? null : trend.value,
          badgeStyle: badge(trend.color),
          meta: this.$t('Reports.RealtimeSensors.stats.lastReadings'),
        },
      ]
    },
    hasChartData() {
      return Array.isArray(this.chartSeries?.[0]?.data) && this.chartSeries[0].data.length > 0
    },
    panelStatusLabel() {
      return this.monitoring
        ? this.$t('Reports.RealtimeSensors.panel.monitoringRealtime')
        : this.$t('Reports.RealtimeSensors.panel.monitorPaused')
    },
    selectedSensorCurrentValue() {
      return this.selectedSensor ? this.sensorCurrentValue(this.selectedSensor) : 0
    },
    selectedSensorRangeState() {
      if (!this.selectedSensor) return 'none'
      return this.sensorRangeState(this.selectedSensor)
    },
    selectedSensorOutOfRange() {
      return ['low', 'high'].includes(this.selectedSensorRangeState)
    },
    selectedSensorRangeLabel() {
      if (!this.selectedSensor) return this.$t('Reports.RealtimeSensors.state.noRangesConfigured')
      return `${this.$t('Reports.RealtimeSensors.state.normal')} ${this.sensorRangeMin(this.selectedSensor).toFixed(0)}-${this.sensorRangeMax(this.selectedSensor).toFixed(0)} ${this.selectedSensor.unidad}`
    },
    selectedSensorStatusLabel() {
      if (!this.selectedSensor) return this.$t('Reports.RealtimeSensors.state.noSensor')
      if (this.selectedSensorRangeState === 'low') return this.$t('Reports.RealtimeSensors.state.belowRange')
      if (this.selectedSensorRangeState === 'high') return this.$t('Reports.RealtimeSensors.state.aboveRange')
      if (this.selectedSensorRangeState === 'no-range') return this.$t('Reports.RealtimeSensors.state.noRange')
      return this.$t('Reports.RealtimeSensors.state.inRange')
    },
    selectedSensorStatusMeta() {
      if (!this.selectedSensor) return this.$t('Reports.RealtimeSensors.state.noRecentReading')
      if (this.selectedSensorRangeState === 'no-range') return this.$t('Reports.RealtimeSensors.state.noLimits')
      const value = this.selectedSensorCurrentValue.toFixed(2)
      return `${value} ${this.selectedSensor.unidad} | ${this.selectedSensorRangeLabel}`
    },
    selectedSensorStatusStyle() {
      if (!this.selectedSensor) return {}
      return {
        color: this.selectedSensorOutOfRange ? '#b42318' : '#0f766e',
      }
    },
    formattedRangeLabel() {
      const selectedWindow = this.ventanas.find(item => item.value === this.ventanaSegundos)
      return selectedWindow ? `Ventana ${selectedWindow.label}` : `Ventana ${this.ventanaSegundos}s`
    },
    rangeLimits() {
      return this.sensorRangeLimits(this.selectedSensor)
    },
    selectedSensorRangoBar() {
      try {
        if (!this.selectedSensor) return null
        return this.sensorRangoBar(this.selectedSensor)
      } catch (e) {
        console.warn('selectedSensorRangoBar error:', e)
        return null
      }
    },
    selectedSensorRangoBarValuePos() {
      try {
        const bar = this.selectedSensorRangoBar
        if (!bar || !bar.ticks || !bar.ticks.length) return null
        const value = this.selectedSensorCurrentValue
        if (!Number.isFinite(value)) return null
        const rango = this.selectedSensor?.rango || this.selectedSensor?.Rango || null
        if (!rango) return null
        const nMin = this._toFiniteNumber(rango.normalMin ?? rango.NormalMin ?? rango.controlMin ?? rango.ControlMin)
        const nMax = this._toFiniteNumber(rango.normalMax ?? rango.NormalMax ?? rango.controlMax ?? rango.ControlMax)
        if (nMin === null || nMax === null) return null
        const aMin = this._toFiniteNumber(rango.advertenciaMin ?? rango.AdvertenciaMin ?? rango.toleranciaMin ?? rango.ToleranciaMin)
        const aMax = this._toFiniteNumber(rango.advertenciaMax ?? rango.AdvertenciaMax ?? rango.toleranciaMax ?? rango.ToleranciaMax)
        const span = (nMax - nMin) || 1
        const lowEdge = aMin !== null ? aMin : nMin
        const highEdge = aMax !== null ? aMax : nMax
        const totalSpan = (highEdge - lowEdge) || 1
        const pad = Math.max(totalSpan * 0.2, span * 0.2, 1)
        const barLow = lowEdge - pad
        const barHigh = highEdge + pad
        const barRange = barHigh - barLow
        if (!Number.isFinite(barRange) || barRange === 0) return null
        const pos = Math.max(0, Math.min(100, ((value - barLow) / barRange) * 100))
        return Number.isFinite(pos) ? pos : null
      } catch (e) {
        console.warn('selectedSensorRangoBarValuePos error:', e)
        return null
      }
    },
    selectedSensorRangoZone() {
      try {
        const rango = this.selectedSensor?.rango || this.selectedSensor?.Rango || null
        if (!rango) return 'unknown'
        const v = this.selectedSensorCurrentValue
        if (!Number.isFinite(v)) return 'unknown'
        const nMin = this._toFiniteNumber(rango.normalMin ?? rango.NormalMin ?? rango.controlMin ?? rango.ControlMin)
        const nMax = this._toFiniteNumber(rango.normalMax ?? rango.NormalMax ?? rango.controlMax ?? rango.ControlMax)
        const aMin = this._toFiniteNumber(rango.advertenciaMin ?? rango.AdvertenciaMin ?? rango.toleranciaMin ?? rango.ToleranciaMin)
        const aMax = this._toFiniteNumber(rango.advertenciaMax ?? rango.AdvertenciaMax ?? rango.toleranciaMax ?? rango.ToleranciaMax)
        if (nMin !== null && nMax !== null && v >= nMin && v <= nMax) return 'normal'
        if (aMin !== null && v >= aMin && nMin !== null && v < nMin) return 'adv'
        if (aMax !== null && nMax !== null && v > nMax && v <= aMax) return 'adv'
        if (aMin !== null && v < aMin) return 'alarma'
        if (aMax !== null && v > aMax) return 'alarma'
        if (nMin !== null && v < nMin) return 'adv'
        if (nMax !== null && v > nMax) return 'adv'
        return 'unknown'
      } catch (e) {
        console.warn('selectedSensorRangoZone error:', e)
        return 'unknown'
      }
    },
    selectedSensorRangoZoneLabel() {
      const map = {
        normal: this.$t('Reports.RealtimeSensors.rango.zoneNormal'),
        adv: this.$t('Reports.RealtimeSensors.rango.zoneWarning'),
        alarma: this.$t('Reports.RealtimeSensors.rango.zoneAlarm'),
        unknown: this.$t('Reports.RealtimeSensors.rango.zoneUnknown'),
      }
      return map[this.selectedSensorRangoZone] || ''
    },
    selectedSensorRangoZoneColor() {
      const map = {
        normal: '#16a34a',
        adv: '#f59e0b',
        alarma: '#dc2626',
        unknown: '#64748b',
      }
      return map[this.selectedSensorRangoZone] || '#64748b'
    },
    chartOptions() {
      const sensor = this.selectedSensor
      if (!sensor) return {}
      const med = this.estadisticas.mediana
      const inferior = this.rangeLimits.inferior
      const superior = this.rangeLimits.superior
      const chartPoints = this.chartSeries?.[0]?.data || []
      const lastPointIndex = Math.max(chartPoints.length - 1, 0)
      const lastPoint = chartPoints.length ? chartPoints[lastPointIndex] : null
      const currentPointTime = lastPoint ? moment(lastPoint.x).valueOf() : null
      const halfWindowMs = Math.max(this.ventanaSegundos, 30) * 500
      const chartWindowMin = Number.isFinite(currentPointTime) ? currentPointTime - halfWindowMs : undefined
      const chartWindowMax = Number.isFinite(currentPointTime) ? currentPointTime + halfWindowMs : undefined
      return {
        chart: {
          id: 'sensor-realtime',
          type: 'area',
          background: 'transparent',
          foreColor: '#52657d',
          animations: { enabled: false },
          toolbar: {
            show: true,
            offsetY: -4,
            tools: {
              download: true,
              selection: false,
              zoom: true,
              zoomin: true,
              zoomout: true,
              pan: false,
              reset: true,
            },
          },
          zoom: { enabled: true, type: 'x' },
        },
        theme: { mode: 'light' },
        colors: ['#0088cc'],
        dataLabels: { enabled: false },
        stroke: { curve: 'straight', width: 2.5, lineCap: 'round' },
        markers: {
          size: 4,
          colors: ['#0088cc'],
          strokeColors: '#fff',
          strokeWidth: 1.5,
          hover: { size: 7, sizeOffset: 2 },
          discrete: [{
            seriesIndex: 0,
            dataPointIndex: lastPointIndex,
            fillColor: '#ef7d00',
            strokeColor: '#ffffff',
            size: 8,
            shape: 'circle',
          }],
        },
        fill: {
          type: 'gradient',
          gradient: {
            shade: 'light',
            type: 'vertical',
            shadeIntensity: 0.2,
            opacityFrom: 0.1,
            opacityTo: 0,
            stops: [0, 80, 100],
          },
        },
        title: { text: undefined },
        subtitle: { text: undefined },
        xaxis: {
          type: 'datetime',
          min: chartWindowMin,
          max: chartWindowMax,
          tickAmount: 8,
          axisBorder: { show: true, color: '#b9c8d8' },
          axisTicks: { show: true, color: '#b9c8d8' },
          labels: {
            datetimeUTC: false,
            format: 'HH:mm:ss',
            hideOverlappingLabels: true,
            style: { fontSize: '11px', fontWeight: 600, colors: '#52657d' },
          },
        },
        yaxis: {
          min: this.chartBounds.min,
          max: this.chartBounds.max,
          tickAmount: 5,
          labels: {
            formatter: v => v?.toFixed(2) + ' ' + sensor.unidad,
            style: { fontSize: '11px', fontWeight: 600, colors: '#52657d' },
          },
        },
        annotations: {
          xaxis: Number.isFinite(currentPointTime) ? [{
            x: currentPointTime,
            borderColor: '#ef7d00',
            strokeDashArray: 4,
            label: {
              orientation: 'horizontal',
              position: 'top',
              offsetY: -4,
              borderColor: '#ef7d00',
              style: {
                color: '#ffffff',
                background: '#ef7d00',
                fontSize: '10px',
                fontWeight: 800,
              },
              text: this.$t('Reports.RealtimeSensors.chart.now'),
            },
          }] : [],
          points: lastPoint ? [{
            x: lastPoint.x,
            y: lastPoint.y,
            marker: {
              size: 8,
              fillColor: '#ef7d00',
              strokeColor: '#ffffff',
              strokeWidth: 4,
              radius: 10,
            },
          }] : [],
          yaxis: [
            ...this.rangeZoneAnnotations,
            ...(med > 0 ? [{
              y: med,
              borderColor: '#67dbe5',
              strokeDashArray: 4,
              label: {
                position: 'right',
                offsetX: 10,
                borderColor: '#22b8cf',
                style: { color: '#fff', background: '#22b8cf', fontSize: '11px', fontWeight: 700 },
                text: `${this.$t('Reports.RealtimeSensors.chart.median')}: ${med.toFixed(2)}${sensor.unidad}`,
              },
            }] : []),
            ...(Number.isFinite(inferior) ? [{
              y: inferior,
              borderColor: '#f59e0b',
              strokeDashArray: 3,
              label: {
                position: 'right',
                offsetX: 10,
                borderColor: '#f59e0b',
                style: { color: '#fff', background: '#f59e0b', fontSize: '10px', fontWeight: 700 },
                text: `${this.$t('Reports.RealtimeSensors.chart.lowerLimit')}: ${inferior.toFixed(2)}${sensor.unidad}`,
              },
            }] : []),
            ...(Number.isFinite(superior) ? [{
              y: superior,
              borderColor: '#ef4444',
              strokeDashArray: 3,
              label: {
                position: 'right',
                offsetX: 10,
                borderColor: '#ef4444',
                style: { color: '#fff', background: '#ef4444', fontSize: '10px', fontWeight: 700 },
                text: `${this.$t('Reports.RealtimeSensors.chart.upperLimit')}: ${superior.toFixed(2)}${sensor.unidad}`,
              },
            }] : []),
          ],
        },
        tooltip: {
          theme: 'dark',
          shared: false,
          intersect: true,
          marker: { show: true },
          x: { format: 'HH:mm:ss' },
          y: { formatter: v => `${v?.toFixed(2)} ${sensor.unidad}` },
        },
        legend: { show: false },
        noData: {
          text: this.$t('Reports.RealtimeSensors.chart.noData'),
          align: 'center',
          verticalAlign: 'middle',
          style: {
            color: '#64748b',
            fontSize: '14px',
            fontWeight: 600,
          },
        },
        grid: {
          borderColor: '#d7e1ec',
          strokeDashArray: 4,
          xaxis: { lines: { show: true } },
          yaxis: { lines: { show: true } },
          padding: { top: 12, right: 20, bottom: 4, left: 10 },
        },
      }
    },
    chartBounds() {
      const valores = (this.chartSeries?.[0]?.data || [])
        .map(point => Number(point?.y))
        .filter(valor => Number.isFinite(valor))

      if (!valores.length) return { min: 0, max: 1 }

      const min = Math.min(...valores)
      const max = Math.max(...valores)
      const center = (min + max) / 2
      const actualSpread = Math.abs(max - min)
      const minimumSpread = Math.max(Math.abs(center || 1) * 0.001, 0.01)
      const spread = Math.max(actualSpread, minimumSpread)
      const padding = spread * 0.12

      return {
        min: min - padding,
        max: max + padding,
      }
    },
    chartAccentStyle() {
      return {
        background: '#ffffff',
        borderColor: '#d7e1ec',
      }
    },

    chartOptionsLegacyUnused() {
      const sensor = this.selectedSensor
      if (!sensor) return {}
      const med = this.estadisticas.mediana
      const inferior = this.rangeLimits.inferior
      const superior = this.rangeLimits.superior
      return {
        chart: {
          id: 'sensor-realtime',
          type: 'line',
          background: 'transparent',
          foreColor: '#475569',
          animations: { enabled: true, easing: 'linear', dynamicAnimation: { speed: 350 } },
          toolbar: {
            show: true,
            tools: {
              download: true,
              selection: false,
              zoom: true,
              zoomin: true,
              zoomout: true,
              pan: false,
              reset: true,
            },
          },
          zoom: { enabled: true, type: 'x' },
        },
        theme: { mode: 'light' },
        colors: ['#9ca3af'],
        dataLabels: { enabled: false },
        stroke: { curve: 'smooth', width: 3, lineCap: 'round' },
        markers: {
          size: 4,
          colors: ['#4a90e2'],
          strokeColors: '#fff',
          strokeWidth: 2,
          hover: { size: 6 },
        },
        plotOptions: {
          bar: {
            columnWidth: '58%',
            borderRadius: 5,
            dataLabels: { position: 'top' },
          },
        },
        fill: {
          type: 'gradient',
          gradient: {
            shade: 'light',
            type: 'vertical',
            shadeIntensity: 0.6,
            opacityFrom: 0.18,
            opacityTo: 0.02,
            stops: [0, 92, 100],
          },
        },
        title: {
          text: `Análisis de Rangos - Token: ${sensor.token || sensor.nombre}`,
          align: 'left',
          margin: 12,
          style: {
            fontSize: '16px',
            fontWeight: 800,
            color: '#111827',
          },
        },
        subtitle: {
          text: `${sensor.unidad} · ${this.formattedRangeLabel}`,
          align: 'left',
          style: {
            fontSize: '12px',
            fontWeight: 600,
            color: '#64748b',
          },
        },
        xaxis: {
          type: 'datetime',
          tickAmount: 6,
          axisBorder: { color: '#eadfbe' },
          axisTicks: { color: '#eadfbe' },
          labels: {
            datetimeUTC: false,
            format: 'HH:mm:ss',
            style: { fontSize: '10px', colors: '#4b5563' },
          },
        },
        yaxis: {
          min: this.chartBounds.min,
          max: this.chartBounds.max,
          tickAmount: 5,
          labels: {
            formatter: v => v?.toFixed(2) + ' ' + sensor.unidad,
            style: { fontSize: '10px', colors: '#4b5563' },
          },
        },
        annotations: {
          yaxis: [
            ...this.rangeZoneAnnotations,
            ...(med > 0 ? [{
              y: med,
              borderColor: '#67dbe5',
              strokeDashArray: 4,
              label: {
                position: 'right',
                offsetX: 10,
                borderColor: '#22b8cf',
                style: { color: '#fff', background: '#22b8cf', fontSize: '11px', fontWeight: 700 },
                text: `${this.$t('Reports.RealtimeSensors.chart.median')}: ${med.toFixed(2)}${sensor.unidad}`,
              },
            }] : []),
            ...(Number.isFinite(inferior) ? [{
              y: inferior,
              borderColor: '#f59e0b',
              strokeDashArray: 3,
              label: {
                position: 'right',
                offsetX: 10,
                borderColor: '#f59e0b',
                style: { color: '#fff', background: '#f59e0b', fontSize: '10px', fontWeight: 700 },
                text: `${this.$t('Reports.RealtimeSensors.chart.lowerLimit')}: ${inferior.toFixed(2)}${sensor.unidad}`,
              },
            }] : []),
            ...(Number.isFinite(superior) ? [{
              y: superior,
              borderColor: '#ef4444',
              strokeDashArray: 3,
              label: {
                position: 'right',
                offsetX: 10,
                borderColor: '#ef4444',
                style: { color: '#fff', background: '#ef4444', fontSize: '10px', fontWeight: 700 },
                text: `${this.$t('Reports.RealtimeSensors.chart.upperLimit')}: ${superior.toFixed(2)}${sensor.unidad}`,
              },
            }] : []),
          ],
        },
        tooltip: {
          theme: 'light',
          shared: false,
          intersect: false,
          x: { format: 'HH:mm:ss' },
          y: { formatter: v => `${v?.toFixed(2)} ${sensor.unidad}` },
        },
        legend: { show: false },
        noData: {
          text: this.$t('Reports.RealtimeSensors.chart.noData'),
          align: 'center',
          verticalAlign: 'middle',
          style: {
            color: '#64748b',
            fontSize: '14px',
            fontWeight: 600,
          },
        },
        grid: {
          borderColor: '#e9dcc0',
          strokeDashArray: 0,
          xaxis: { lines: { show: false } },
          yaxis: { lines: { show: true } },
          padding: { top: 8, right: 18, bottom: 0, left: 10 },
        },
      }
    },
    rangeZoneAnnotations() {
      const inferior = this.rangeLimits.inferior
      const superior = this.rangeLimits.superior
      const min = this.chartBounds.min
      const max = this.chartBounds.max
      const zones = []

      if (Number.isFinite(inferior) && inferior > min) {
        zones.push({
          y: min,
          y2: inferior,
          fillColor: 'rgba(187, 247, 208, 0.35)',
          borderColor: 'transparent',
          opacity: 0.45,
        })
      }

      if (Number.isFinite(inferior) && Number.isFinite(superior) && superior > inferior) {
        zones.push({
          y: inferior,
          y2: superior,
          fillColor: 'rgba(254, 240, 138, 0.28)',
          borderColor: 'transparent',
          opacity: 0.55,
        })
      }

      if (Number.isFinite(superior) && max > superior) {
        zones.push({
          y: superior,
          y2: max,
          fillColor: 'rgba(254, 202, 202, 0.22)',
          borderColor: 'transparent',
          opacity: 0.42,
        })
      }

      return zones
    },
  },

  watch: {
    ventanaSegundos() {
      if (this.monitoring) {
        this.detenerPolling()
        this.iniciarPolling()
      }
    },
  },

  mounted() {
    const savedLocale = localStorage.getItem('locale')
    if (savedLocale) this.$i18n.locale = savedLocale
    this.lanzarIntro()
    this.initializeReport()
  },

  beforeDestroy() {
    this.detenerPolling()
    if (this.initialDataAbortController) {
      this.initialDataAbortController.abort()
      this.initialDataAbortController = null
    }
    if (this.introSplashTimeout) {
      clearTimeout(this.introSplashTimeout)
      this.introSplashTimeout = null
    }
  },

  methods: {
    setLocale(locale) {
      if (!locale) return
      this.$i18n.locale = locale
      localStorage.setItem('locale', locale)
    },
    async initializeReport() {
      await this.cargarAreas()
      await this.precargarResumenes()
    },
    requestConfigRealtimeVive(idMaquinaIns) {
      return axios.get('/SensorDatos/ConfigRealtimeVive', { params: { idMaquinaIns } })
    },
    requestValoresRealtimeVive(params, config = {}) {
      return axios.get('/SensorDatos/ValoresRealtimeVive', { ...config, params })
    },
    async fetchRealtimeConfigForMachine(idMaquinaIns) {
      if (!idMaquinaIns) return null
      const resp = await this.requestConfigRealtimeVive(idMaquinaIns)
      return resp.data || null
    },
    async fetchRealtimeValuesForMachine(idMaquinaIns, seconds = null, keys = null) {
      if (!idMaquinaIns) return []
      const windowSeconds = seconds || Math.max(this.ventanaSegundos || 0, 300)
      const inicio = moment().subtract(windowSeconds, 'seconds').format('YYYY-MM-DD HH:mm:ss')
      const fin = moment().format('YYYY-MM-DD HH:mm:ss')
      const params = { idMaquinaIns, inicio, fin }
      if (keys) params.keys = Array.isArray(keys) ? keys.join(',') : keys
      const resp = await this.requestValoresRealtimeVive(params)
      return this.normalizeValuesCollection(resp.data?.values || resp.data?.Values || [])
    },
    realtimeSensorKeys() {
      return (this.sensores || [])
        .map(sensor => sensor?.clavePayload || sensor?.token)
        .filter(Boolean)
    },
    lanzarIntro() {
      this.showIntroSplash = true
      if (this.introSplashTimeout) clearTimeout(this.introSplashTimeout)
      this.introSplashTimeout = setTimeout(() => {
        this.showIntroSplash = false
        this.introSplashTimeout = null
      }, 1250)
    },
    stepIconForIndex(step) {
      const icons = {
        1: 'mdi-domain',
        2: 'mdi-robot-industrial',
        3: 'mdi-radar',
      }
      return icons[step] || 'mdi-radiobox-blank'
    },
    getMachineIcon(maq) {
      const text = `${maq?.nombre || ''} ${maq?.marca || ''} ${maq?.modelo || ''} ${maq?.estado || ''}`.toUpperCase()
      if (text.includes('ROBOT') || text.includes('FANUC') || text.includes('KUKA')) return 'mdi-robot-industrial'
      if (text.includes('COMPRESOR')) return 'mdi-fan'
      if (text.includes('VISION') || text.includes('COGNEX')) return 'mdi-camera-iris'
      if (text.includes('EXTRUS')) return 'mdi-chart-areaspline'
      if (text.includes('ENSAMBLE')) return 'mdi-tools'
      return 'mdi-cog-outline'
    },
    resolveCount(source, keys = []) {
      if (!source || typeof source !== 'object') return null
      for (const key of keys) {
        const value = source[key]
        if (Array.isArray(value)) return value.length
        const parsed = Number(value)
        if (Number.isFinite(parsed)) return parsed
      }
      return null
    },
    resolveAreaMachineCount(area) {
      return this.resolveCount(area, [
        'maquinas',
        'Maquinas',
        'cantidadMaquinas',
        'CantidadMaquinas',
        'totalMaquinas',
        'TotalMaquinas',
        'numeroMaquinas',
        'NumeroMaquinas',
        'countMaquinas',
        'CountMaquinas',
      ])
    },
    resolveMachineSensorCount(machine) {
      return this.resolveCount(machine, [
        'sensores',
        'Sensores',
        'maquinaSensores',
        'MaquinaSensores',
        'cantidadSensores',
        'CantidadSensores',
        'totalSensores',
        'TotalSensores',
        'numeroSensores',
        'NumeroSensores',
        'countSensores',
        'CountSensores',
      ])
    },
    updateAreaMachineCount(idArea, count) {
      const normalized = Number(count)
      if (!idArea || !Number.isFinite(normalized)) return
      this.areas = this.areas.map(area => (
        area.idArea === idArea
          ? { ...area, maquinas: normalized }
          : area
      ))
      if (this.selectedArea?.idArea === idArea) {
        this.selectedArea = { ...this.selectedArea, maquinas: normalized }
      }
    },
    updateMachineSensorCount(idMaquinaIns, count) {
      const normalized = Number(count)
      if (!idMaquinaIns || !Number.isFinite(normalized)) return
      Vue.set(this.sensorCountByMachine, idMaquinaIns, normalized)
      this.maquinas = this.maquinas.map(machine => (
        machine.idMaquinaIns === idMaquinaIns
          ? { ...machine, sensores: normalized }
          : machine
      ))
      if (this.selectedMaquina?.idMaquinaIns === idMaquinaIns) {
        this.selectedMaquina = { ...this.selectedMaquina, sensores: normalized }
      }
    },
    async precargarResumenes() {
      try {
        const resp = await axios.get('/MaquinaInstancias')
        const allMachines = Array.isArray(resp.data) ? resp.data : []
        const machinesByArea = allMachines.reduce((acc, machine) => {
          const areaId = machine.idArea || machine.IdArea
          if (!areaId) return acc
          if (!acc[areaId]) acc[areaId] = []
          acc[areaId].push(machine)
          return acc
        }, {})

        this.areas = this.areas.map(area => ({
          ...area,
          maquinas: machinesByArea[area.idArea]?.length ?? area.maquinas ?? 0,
        }))

        if (this.selectedArea?.idArea && machinesByArea[this.selectedArea.idArea]) {
          this.maquinas = this.decorateMaquinasList(machinesByArea[this.selectedArea.idArea])
        }

        await Promise.allSettled(
          allMachines.map(async machine => {
            const idMaquinaIns = machine.idMaquinaIns || machine.IdMaquinaIns
            if (!idMaquinaIns) return
            const config = await this.fetchRealtimeConfigForMachine(idMaquinaIns)
            const sensores = config?.sensores || config?.Sensores || []
            this.updateMachineSensorCount(idMaquinaIns, Array.isArray(sensores) ? sensores.length : 0)
          })
        )
      } catch (e) {
        console.error('Error precargando resumenes de sensores en tiempo real:', e)
      }
    },
    decorateMaquinasList(maquinas = []) {
      return (Array.isArray(maquinas) ? maquinas : []).map(m => ({
        ...m,
        estado: m.estado || m.Estado || 'Corriendo',
        sensores: this.sensorCountByMachine[m.idMaquinaIns || m.IdMaquinaIns] ?? this.resolveMachineSensorCount(m) ?? 0,
      }))
    },
    alphaColor(color, alpha = 0.18) {
      if (!color) return `rgba(37, 99, 235, ${alpha})`
      if (color.startsWith('#')) {
        const hex = color.replace('#', '')
        const full = hex.length === 3 ? hex.split('').map(c => c + c).join('') : hex
        const int = Number.parseInt(full, 16)
        const r = (int >> 16) & 255
        const g = (int >> 8) & 255
        const b = int & 255
        return `rgba(${r}, ${g}, ${b}, ${alpha})`
      }
      return color
    },
    hexFromColor(color, alpha = 0.22) {
      const palette = {
        'blue darken-2': `rgba(25, 118, 210, ${alpha})`,
        'green darken-2': `rgba(46, 125, 50, ${alpha})`,
        'orange darken-2': `rgba(239, 108, 0, ${alpha})`,
        'purple darken-2': `rgba(106, 27, 154, ${alpha})`,
        'teal darken-2': `rgba(0, 121, 107, ${alpha})`,
        'red darken-2': `rgba(198, 40, 40, ${alpha})`,
        'orange darken-1': `rgba(251, 140, 0, ${alpha})`,
        grey: `rgba(100, 116, 139, ${alpha})`,
      }
      return palette[color] || this.alphaColor(color, alpha)
    },
    hexFromStatus(estado, alpha = 0.2) {
      return this.hexFromColor(this.getStatusColor(estado), alpha)
    },
    isCompletedFlow(stepIndex) {
      return this.step === 3 && stepIndex <= 3
    },
    stepState(stepIndex) {
      if (this.isCompletedFlow(stepIndex)) return 'done'
      if (this.step === stepIndex) return 'active'
      if (this.step > stepIndex) return 'visited'
      return 'idle'
    },
    stepAccentColor(stepIndex) {
      const palette = {
        1: '#2f7de1',
        2: '#ef4b5a',
        3: '#f28a00',
      }
      const doneColor = '#0f766e'
      return this.isCompletedFlow(stepIndex) ? doneColor : (palette[stepIndex] || '#2f7de1')
    },
    stepCardStyle(stepIndex) {
      const color = this.stepAccentColor(stepIndex)
      return {
        borderColor: this.alphaColor(color, 0.38),
      }
    },
    stepIconStyle(stepIndex) {
      return {
        color: this.stepAccentColor(stepIndex),
      }
    },
    stepIconShellStyle(stepIndex) {
      const color = this.stepAccentColor(stepIndex)
      return {
        background: `linear-gradient(180deg, ${this.alphaColor(color, 0.2)} 0%, ${this.alphaColor(color, 0.12)} 100%)`,
        borderColor: this.alphaColor(color, 0.24),
      }
    },
    stepNumberStyle(stepIndex) {
      return {
        color: this.stepAccentColor(stepIndex),
      }
    },

    // ─── Navegación ──────────────────────────────────────────────────────────
    stepDone(n) {
      if (n === 1) return !!this.selectedArea
      if (n === 2) return !!this.selectedMaquina
      if (n === 3) return !!this.selectedSensor
      return false
    },
    canGoStep(n) {
      if (n === 1) return true
      if (n === 2) return !!this.selectedArea
      if (n === 3) return !!this.selectedMaquina
      return false
    },
    goStep(n) {
      if (!this.canGoStep(n)) return
      if (n < this.step) {
        this.detenerPolling()
        if (n === 1) { this.selectedArea = null; this.selectedMaquina = null; this.selectedSensor = null; this.sensorGroups = []; this.sensores = [] }
        if (n === 2) { this.selectedMaquina = null; this.selectedSensor = null; this.sensorGroups = []; this.sensores = [] }
        if (n === 3) { this.selectedSensor = null }
      }
      this.step = n
    },

    // ─── Selección ───────────────────────────────────────────────────────────
    selectArea(area) {
      this.detenerPolling()
      if (this.initialDataAbortController) this.initialDataAbortController.abort()
      this.selectedArea = area
      this.selectedMaquina = null
      this.selectedSensor = null
      this.sensorGroups = []
      this.sensores = []
      this.step = 2
      this.cargarMaquinas(area.idArea)
    },

    selectMaquina(maq) {
      this.detenerPolling()
      if (this.initialDataAbortController) this.initialDataAbortController.abort()
      this.selectedMaquina = maq
      this.selectedSensor = null
      this.sensorGroups = []
      this.sensores = []
      this.machineRealtime = null
      this.step = 3
      this.cargarSensores(maq.idMaquinaIns)
    },

    selectSensor(sensor) {
      this.detenerPolling()
      this.selectedSensor = sensor
      this.machineRealtime = null
      this.chartMontado = false
      this.chartSeries = [
        { name: sensor.nombre, data: [] },
      ]
      // Si ya hay historial para este token, lo usa
      if (!this.sensorHistorial[sensor.token]) {
        Vue.set(this.sensorHistorial, sensor.token, [])
      }
      this.$nextTick(async () => {
        this.chartMontado = true
        await this.cargarDatosIniciales()
        // Auto-iniciar el monitoreo al seleccionar/cambiar de sensor
        if (this.selectedSensor && this.selectedSensor.token === sensor.token) {
          this.monitoring = true
          this.iniciarPolling()
        }
      })
    },

    // ─── API Calls ────────────────────────────────────────────────────────────
    async cargarAreas() {
      try {
        const resp = await axios.get('/Areas')
        // Enriquecer con metadatos de UI
        this.areas = resp.data.map((a, i) => ({
          ...a,
          color: ['blue darken-2','green darken-2','orange darken-2','purple darken-2','teal darken-2','red darken-2'][i % 6],
          icon: ['mdi-domain','mdi-robot-industrial','mdi-shield-check','mdi-package-variant','mdi-flash','mdi-tools'][i % 6],
          descripcion: a.descripcion || this.$t('Reports.RealtimeSensors.area.defaultDescription'),
          status: this.$t('Reports.RealtimeSensors.area.active'),
          statusColor: 'success',
          maquinas: this.resolveAreaMachineCount(a) ?? 0,
        }))
      } catch (e) {
        console.error('Error cargando áreas:', e)
        this.areas = []
      }
    },

    async cargarMaquinas(idArea) {
      this.loadingMaquinas = true
      this.maquinas = []
      try {
        const resp = await axios.get('/MaquinaInstancias', { params: { idArea } })
        this.maquinas = this.decorateMaquinasList(resp.data)
        this.updateAreaMachineCount(idArea, this.maquinas.length)
      } catch (e) {
        console.error('Error cargando máquinas:', e)
        this.maquinas = []
      } finally {
        this.loadingMaquinas = false
      }
    },

    async cargarSensores(idMaquinaIns) {
      this.loadingSensores = true
      this.sensorGroups = []
      this.sensores = []
      try {
        const config = await this.fetchRealtimeConfigForMachine(idMaquinaIns)
        const items = Array.isArray(config?.sensores || config?.Sensores) ? (config.sensores || config.Sensores) : []
        this.machineRealtime = config
        this.sensorGroups = items.map((sensor, i) => this.normalizeSensorGroupRealtime(sensor, i))
        this.sensores = this.sensorGroups.flatMap(sensor => sensor.variables)
        this.updateMachineSensorCount(idMaquinaIns, this.sensorGroups.length)
        this.sensores.forEach(s => {
          if (!this.sensorHistorial[s.token]) {
            Vue.set(this.sensorHistorial, s.token, [])
          }
          const initialValue = Number.isFinite(Number(s.valorActual)) ? Number(s.valorActual) : s.base
          Vue.set(this.liveValues, s.token, initialValue)
        })
        await this.hidratarLecturasSensores()
      } catch (e) {
        console.error('Error cargando sensores:', e)
        this.sensorGroups = []
        this.sensores = []
        this.updateMachineSensorCount(idMaquinaIns, 0)
      } finally {
        this.loadingSensores = false
      }
    },

    async cargarDatosIniciales() {
      if (!this.selectedSensor || !this.selectedMaquina) return
      if (this.initialDataAbortController) this.initialDataAbortController.abort()
      const controller = new AbortController()
      this.initialDataAbortController = controller
      const selectedToken = this.selectedSensor.token
      try {
        const inicio = moment().subtract(this.ventanaSegundos, 'seconds').format('YYYY-MM-DD HH:mm:ss')
        const fin = moment().format('YYYY-MM-DD HH:mm:ss')
        const resp = await this.requestValoresRealtimeVive({
          idMaquinaIns: this.selectedMaquina.idMaquinaIns,
          inicio,
          fin,
          keys: this.selectedSensor.clavePayload || this.selectedSensor.token,
        }, { signal: controller.signal, timeout: 30000 })
        if (controller.signal.aborted || this.selectedSensor?.token !== selectedToken) return
        this.applyRealtimeResponse(resp.data)
      } catch (e) {
        if (axios.isCancel(e) || e?.code === 'ERR_CANCELED') return
        console.error('Error cargando datos iniciales del sensor:', e)
        if (!this.selectedSensor || this.selectedSensor.token !== selectedToken) return
        Vue.set(this.sensorHistorial, selectedToken, [])
        this.actualizarChart()
      } finally {
        if (this.initialDataAbortController === controller) this.initialDataAbortController = null
      }
    },
    async hidratarLecturasSensores(valuesItems = null) {
      const sensores = Array.isArray(this.sensores) ? this.sensores : []
      if (!sensores.length) return
      let valuesRaw = this.normalizeValuesCollection(valuesItems)
      if (!valuesRaw.length && this.selectedMaquina?.idMaquinaIns) {
        valuesRaw = await this.fetchRealtimeValuesForMachine(
          this.selectedMaquina.idMaquinaIns,
          Math.max(this.ventanaSegundos, 300),
          this.realtimeSensorKeys()
        )
      }

      sensores.forEach(sensor => {
        const puntos = this.normalizeValuePoints(valuesRaw, sensor.clavePayload || sensor.token)
        Vue.set(this.sensorHistorial, sensor.token, puntos)
        if (puntos.length) {
          Vue.set(this.liveValues, sensor.token, puntos[puntos.length - 1].y)
        }
      })

      this.$nextTick(() => this.sensores.forEach(sensor => this.dibujarSparkline(sensor)))
    },

    async obtenerNuevosDatos() {
      if (!this.selectedSensor || !this.selectedMaquina || !this.monitoring || this.pollingInFlight) return
      this.pollingInFlight = true
      const controller = new AbortController()
      this.pollingAbortController = controller
      try {
        const inicio = moment().subtract(this.ventanaSegundos, 'seconds').format('YYYY-MM-DD HH:mm:ss')
        const fin = moment().format('YYYY-MM-DD HH:mm:ss')
        const resp = await this.requestValoresRealtimeVive({
          idMaquinaIns: this.selectedMaquina.idMaquinaIns,
          inicio,
          fin,
          keys: this.realtimeSensorKeys().join(','),
        }, { signal: controller.signal, timeout: 30000 })
        if (controller.signal.aborted) return
        this.applyRealtimeResponse(resp.data)
      } catch (e) {
        if (axios.isCancel(e) || e?.code === 'ERR_CANCELED') return
        console.error('Error actualizando datos del sensor:', e)
        this.actualizarChart()
      } finally {
        if (this.pollingAbortController === controller) {
          this.pollingAbortController = null
          this.pollingInFlight = false
        }
      }
    },

    // ─── Chart & Stats ────────────────────────────────────────────────────────
    applyRealtimeResponse(payload = {}) {
      const valuesRaw = this.normalizeValuesCollection(payload?.values || payload?.Values || [])
      if (payload?.idMaquinaIns || payload?.IdMaquinaIns || payload?.machineId || payload?.MachineId) {
        this.machineRealtime = {
          ...(this.machineRealtime || {}),
          idMaquinaIns: payload.idMaquinaIns || payload.IdMaquinaIns || this.machineRealtime?.idMaquinaIns,
          machineId: payload.machineId || payload.MachineId || this.machineRealtime?.machineId,
        }
      }

      const currentSensors = this.sensores || []
      currentSensors.forEach(sensor => {
        const key = sensor.clavePayload || sensor.token
        if (!this.valuesContainKey(valuesRaw, key)) return
        const puntos = this.normalizeValuePoints(valuesRaw, key)
        Vue.set(this.sensorHistorial, sensor.token, puntos)
        if (puntos.length) {
          Vue.set(this.liveValues, sensor.token, puntos[puntos.length - 1].y)
        }
      })

      if (this.selectedSensor && !valuesRaw.length) {
        Vue.set(this.sensorHistorial, this.selectedSensor.token, [])
      }

      this.actualizarChart()
    },
    normalizeValuesCollection(values = []) {
      return Array.isArray(values) ? values : []
    },
    normalizeValuePoints(values = [], key = '') {
      if (!key) return []
      return this.normalizeValuesCollection(values)
        .map(row => ({ x: Number(row.fecha ?? row.Fecha), y: Number(row[key]) }))
        .filter(point => point.x > 0 && Number.isFinite(point.x) && Number.isFinite(point.y))
    },
    valuesContainKey(values = [], key = '') {
      if (!key) return false
      return this.normalizeValuesCollection(values).some(row => Object.prototype.hasOwnProperty.call(row, key))
    },
    isSameSensor(a, b) {
      if (!a || !b) return false
      const aToken = `${a.clavePayload || a.token || a.Token || ''}`
      const bToken = `${b.clavePayload || b.token || b.Token || ''}`
      if (aToken && bToken && aToken === bToken) return true
      const keys = ['idMaquinaSensorVariable', 'idSensorVariable', 'idSensor']
      return keys.some(key => Number(a[key] || 0) && Number(a[key] || 0) === Number(b[key] || 0))
    },
    sensorCurrentValue(sensor) {
      const liveValue = this.liveValues[sensor?.token]
      if (liveValue !== undefined && Number.isFinite(Number(liveValue))) return Number(liveValue)
      if (sensor?.valorActual !== null && sensor?.valorActual !== undefined && Number.isFinite(Number(sensor.valorActual))) {
        return Number(sensor.valorActual)
      }
      return Number(sensor?.base) || 0
    },
    sensorRangeLimits(sensor) {
      const rango = sensor?.rango || sensor?.Rango || null
      if (rango) {
        // Lee campos nuevos primero, con fallback a los antiguos
        const inferior = Number(
          rango.advertenciaMin ?? rango.AdvertenciaMin ??
          rango.normalMin ?? rango.NormalMin ??
          rango.controlMin ?? rango.ControlMin ??
          rango.toleranciaMin ?? rango.ToleranciaMin ??
          rango.alarmaMin ?? rango.AlarmaMin
        )
        const superior = Number(
          rango.advertenciaMax ?? rango.AdvertenciaMax ??
          rango.normalMax ?? rango.NormalMax ??
          rango.controlMax ?? rango.ControlMax ??
          rango.toleranciaMax ?? rango.ToleranciaMax ??
          rango.alarmaMax ?? rango.AlarmaMax
        )
        return {
          inferior: Number.isFinite(inferior) ? inferior : null,
          superior: Number.isFinite(superior) ? superior : null,
        }
      }

      const rangos = Array.isArray(sensor?.rangos) ? sensor.rangos : []
      const inferiores = rangos
        .map(rango => Number(rango.desviacionInferior ?? rango.DesviacionInferior))
        .filter(valor => Number.isFinite(valor))
      const superiores = rangos
        .map(rango => Number(rango.desviacionSuperior ?? rango.DesviacionSuperior))
        .filter(valor => Number.isFinite(valor))

      return {
        inferior: inferiores.length ? Math.min(...inferiores) : null,
        superior: superiores.length ? Math.max(...superiores) : null,
      }
    },
    sensorRangeState(sensor) {
      if (!sensor) return 'none'
      const value = this.sensorCurrentValue(sensor)
      const { inferior, superior } = this.sensorRangeLimits(sensor)
      if (!Number.isFinite(inferior) && !Number.isFinite(superior)) return 'no-range'
      if (Number.isFinite(inferior) && value < inferior) return 'low'
      if (Number.isFinite(superior) && value > superior) return 'high'
      return 'ok'
    },
    sensorOutOfRange(sensor) {
      return ['low', 'high'].includes(this.sensorRangeState(sensor))
    },
    sensorRangeMin(sensor) {
      const { inferior } = this.sensorRangeLimits(sensor)
      if (Number.isFinite(inferior)) return inferior

      const rangos = Array.isArray(sensor?.rangos) ? sensor.rangos : []
      const limites = rangos
        .map(rango => Number(rango.desviacionInferior ?? rango.DesviacionInferior))
        .filter(valor => Number.isFinite(valor))
      if (limites.length) return Math.min(...limites)
      const actual = this.sensorCurrentValue(sensor)
      return Math.max(0, actual * 0.6)
    },
    sensorRangeMax(sensor) {
      const { superior } = this.sensorRangeLimits(sensor)
      if (Number.isFinite(superior)) return superior

      const rangos = Array.isArray(sensor?.rangos) ? sensor.rangos : []
      const limites = rangos
        .map(rango => Number(rango.desviacionSuperior ?? rango.DesviacionSuperior))
        .filter(valor => Number.isFinite(valor))
      if (limites.length) return Math.max(...limites)
      const actual = this.sensorCurrentValue(sensor)
      return Math.max(actual * 1.15, 1)
    },
    sensorProgressPercent(sensor) {
      const actual = this.sensorCurrentValue(sensor)
      const min = this.sensorRangeMin(sensor)
      const max = this.sensorRangeMax(sensor)
      const span = Math.max(max - min, 0.0001)
      const raw = ((actual - min) / span) * 100
      return Math.max(0, Math.min(100, raw))
    },
    normalizeSensorGroupRealtime(item = {}, index = 0) {
      const variablesRaw = Array.isArray(item.variables || item.Variables)
        ? (item.variables || item.Variables)
        : []
      const idSensor = item.idSensor || item.IdSensor || item.sensorId || item.SensorId || index + 1
      const sensorCodigo = `${item.sensorCodigo || item.SensorCodigo || item.codigo || item.Codigo || item.clavePayload || item.ClavePayload || `SENSOR-${idSensor}`}`.trim()
      const sensorNombre = `${item.sensorNombre || item.SensorNombre || item.nombre || item.Nombre || item.nombreTipoSensor || item.NombreTipoSensor || this.tokenToNombre(sensorCodigo)}`.trim()
      const meta = this.inferSensorUiMeta(`${sensorNombre} ${sensorCodigo}`)
      const variables = (variablesRaw.length ? variablesRaw : [item]).map((variable, variableIndex) => (
        this.normalizeSensorRealtime(
          {
            ...variable,
            idSensor,
            IdSensor: idSensor,
            sensorCodigo,
            SensorCodigo: sensorCodigo,
            sensorNombre,
            SensorNombre: sensorNombre,
          },
          variableIndex,
          index
        )
      ))

      return {
        ...item,
        idSensor,
        sensorCodigo,
        sensorNombre,
        variables,
        icon: meta.icon,
        color: meta.color,
        colorBg: meta.colorBg,
      }
    },
    normalizeSensorRealtime(item = {}, index = 0) {
      const idSensor = item.idSensor || item.IdSensor || item.sensorId || item.SensorId || index + 1
      const token = `${item.clavePayload || item.ClavePayload || item.tokenDispositivo || item.TokenDispositivo || item.token || item.Token || `sensor-${idSensor}`}`.trim()
      const tipo = `${item.variableNombre || item.VariableNombre || item.sensorNombre || item.SensorNombre || item.nombreTipoSensor || item.NombreTipoSensor || item.tipoSensorNombre || item.TipoSensorNombre || item.tipoSensor?.nombre || item.TipoSensor?.Nombre || ''}`.trim()
      const descripcion = `${item.name || item.Name || item.descripcion || item.Descripcion || ''}`.trim()
      const nombreMaquina = `${item.nombreMaquina || item.NombreMaquina || this.selectedMaquina?.nombre || ''}`.trim()
      const unidad = `${item.unidadAbreviatura || item.UnidadAbreviatura || item.unidadNombre || item.UnidadNombre || item.unidad || item.Unidad || item.tipoSensor?.unidad?.abreviatura || item.TipoSensor?.Unidad?.Abreviatura || 'u'}`.trim()
      const points = []
      const valorActualRaw = item.valorActual ?? item.ValorActual ?? (points.length ? points[points.length - 1].y : null)
      const rangos = Array.isArray(item.rangos || item.Rangos) ? (item.rangos || item.Rangos) : []
      const rango = item.rango || item.Rango || null
      const nombre = descripcion || tipo || this.tokenToNombre(token)
      const fueraDeRango = Boolean(item.fueraDeRango ?? item.FueraDeRango ?? this.isValueOutOfRange(valorActualRaw, rango))
      const meta = this.inferSensorUiMeta(tipo || descripcion || token, unidad, fueraDeRango)

      return {
        ...item,
        idSensor,
        idMaquinaSensorVariable: item.idMaquinaSensorVariable || item.IdMaquinaSensorVariable || null,
        idSensorVariable: item.idSensorVariable || item.IdSensorVariable || null,
        idVariable: item.idVariable || item.IdVariable || null,
        sensorCodigo: item.sensorCodigo || item.SensorCodigo || null,
        sensorNombre: item.sensorNombre || item.SensorNombre || null,
        variableCodigo: item.variableCodigo || item.VariableCodigo || null,
        variableNombre: item.variableNombre || item.VariableNombre || null,
        clavePayload: token,
        idMaquinaIns: item.idMaquinaIns || item.IdMaquinaIns || this.selectedMaquina?.idMaquinaIns || null,
        nombreMaquina,
        idTipoSensor: item.idTipoSensor || item.IdTipoSensor || null,
        nombreTipoSensor: tipo,
        descripcion,
        nombre,
        unidad,
        token,
        tokenDispositivo: token,
        valorActual: valorActualRaw === null || valorActualRaw === undefined || valorActualRaw === '' ? null : Number(valorActualRaw),
        fechaValorActual: item.fechaValorActual || item.FechaValorActual || null,
        fueraDeRango,
        rangos,
        rango,
        ...meta,
      }
    },
    sensorRangoBar(sensor) {
      const rango = sensor?.rango || sensor?.Rango || null
      if (!rango) return null
      // Fallback a campos antiguos por compatibilidad
      const nMin = this._toFiniteNumber(
        rango.normalMin ?? rango.NormalMin ??
        rango.controlMin ?? rango.ControlMin
      )
      const nMax = this._toFiniteNumber(
        rango.normalMax ?? rango.NormalMax ??
        rango.controlMax ?? rango.ControlMax
      )
      if (nMin === null || nMax === null || nMin > nMax) return null
      const rawAMin = this._toFiniteNumber(
        rango.advertenciaMin ?? rango.AdvertenciaMin ??
        rango.toleranciaMin ?? rango.ToleranciaMin ??
        rango.alarmaMin ?? rango.AlarmaMin
      )
      const rawAMax = this._toFiniteNumber(
        rango.advertenciaMax ?? rango.AdvertenciaMax ??
        rango.toleranciaMax ?? rango.ToleranciaMax ??
        rango.alarmaMax ?? rango.AlarmaMax
      )
      const aMin = rawAMin !== null && rawAMin <= nMin ? rawAMin : null
      const aMax = rawAMax !== null && rawAMax >= nMax ? rawAMax : null

      const span = (nMax - nMin) || 1
      const lowEdge = aMin !== null ? aMin : nMin
      const highEdge = aMax !== null ? aMax : nMax
      const totalSpan = (highEdge - lowEdge) || 1
      const pad = Math.max(totalSpan * 0.2, span * 0.2, 1)
      const barLow = lowEdge - pad
      const barHigh = highEdge + pad
      const barRange = barHigh - barLow
      const pos = (v) => Math.max(0, Math.min(100, ((v - barLow) / barRange) * 100))
      const result = {
        segments: [],
        ticks: [],
        normal: { lower: nMin, upper: nMax },
        advBaja: aMin !== null ? { lower: aMin, upper: nMin } : null,
        advAlta: aMax !== null ? { lower: nMax, upper: aMax } : null,
        alarmaBaja: aMin !== null ? { lower: barLow, upper: aMin } : null,
        alarmaAlta: aMax !== null ? { lower: aMax, upper: barHigh } : null,
      }

      if (aMin !== null) {
        result.segments.push({ kind: 'alarma', flex: Math.max(0.5, aMin - barLow) })
        result.segments.push({ kind: 'adv', flex: Math.max(0.5, nMin - aMin) })
      }
      result.segments.push({ kind: 'normal', flex: Math.max(0.5, (nMax - nMin) || 1) })
      if (aMax !== null) {
        result.segments.push({ kind: 'adv', flex: Math.max(0.5, aMax - nMax) })
        result.segments.push({ kind: 'alarma', flex: Math.max(0.5, barHigh - aMax) })
      }

      if (aMin !== null) result.ticks.push({ value: aMin, pos: pos(aMin) })
      result.ticks.push({ value: nMin, pos: pos(nMin) })
      result.ticks.push({ value: nMax, pos: pos(nMax) })
      if (aMax !== null) result.ticks.push({ value: aMax, pos: pos(aMax) })

      return result
    },
    _toFiniteNumber(value) {
      if (value === null || value === undefined || value === '') return null
      const n = Number(value)
      return Number.isFinite(n) ? n : null
    },
    formatRangoNum(value) {
      if (value === null || value === undefined || value === '') return '—'
      const num = Number(value)
      if (!Number.isFinite(num)) return '—'
      return num.toFixed(2)
    },
    isValueOutOfRange(value, rango = null) {
      if (value === null || value === undefined || value === '' || !rango) return false
      const numeric = Number(value)
      if (!Number.isFinite(numeric)) return false
      const min = Number(
        rango.normalMin ?? rango.NormalMin ??
        rango.controlMin ?? rango.ControlMin ??
        rango.toleranciaMin ?? rango.ToleranciaMin
      )
      const max = Number(
        rango.normalMax ?? rango.NormalMax ??
        rango.controlMax ?? rango.ControlMax ??
        rango.toleranciaMax ?? rango.ToleranciaMax
      )
      if (Number.isFinite(min) && numeric < min) return true
      if (Number.isFinite(max) && numeric > max) return true
      return false
    },
    inferSensorUiMeta(text = '', unidad = 'u', fueraDeRango = false) {
      const t = `${text} ${unidad}`.toUpperCase()
      let icon = 'mdi-sine-wave'
      let color = '#2563eb'
      let colorBg = '#eff6ff'
      let tagKey = 'sensor'
      let tagColor = 'blue darken-2'
      let base = 10

      if (t.includes('TEMP') || t.includes('°C') || t.includes('CELSIUS')) {
        icon = 'mdi-thermometer'
        color = '#d97706'
        colorBg = '#fffbeb'
        tagKey = 'temperature'
        tagColor = 'orange darken-2'
        base = 220
      } else if (t.includes('PRES') || t.includes('BAR') || t.includes('PSI')) {
        icon = 'mdi-gauge'
        color = '#7c3aed'
        colorBg = '#f5f3ff'
        tagKey = 'pressure'
        tagColor = 'purple darken-2'
        base = 85
      } else if (t.includes('RPM')) {
        icon = 'mdi-rotate-3d-variant'
        color = '#0891b2'
        colorBg = '#ecfeff'
        tagKey = 'rpm'
        tagColor = 'cyan darken-2'
        base = 145
      } else if (t.includes('AMP') || t.includes('CORRIENTE') || t.includes(' A ')) {
        icon = 'mdi-lightning-bolt'
        color = '#16a34a'
        colorBg = '#f0fdf4'
        tagKey = 'electrical'
        tagColor = 'green darken-2'
        base = 3.8
      } else if (t.includes('CICLO') || t.includes('TIEMPO') || t.includes('SEG') || t.includes('SEC')) {
        icon = 'mdi-clock-outline'
        color = '#2563eb'
        colorBg = '#eff6ff'
        tagKey = 'cycle'
        tagColor = 'blue darken-2'
        base = 18
      }

      if (fueraDeRango) {
        tagColor = 'red darken-2'
      }

      const tag = this.$t(`Reports.RealtimeSensors.categories.${tagKey}`)
      return { icon, color, colorBg, tag, tagKey, tagColor, base }
    },
    actualizarChart() {
      if (!this.selectedSensor || !this.chartMontado) return
      const token   = this.selectedSensor.token
      const hist    = this.sensorHistorial[token] || []
      // Actualizar liveValue
      if (hist.length > 0) {
        Vue.set(this.liveValues, token, hist[hist.length - 1].y)
      }

      this.chartSeries = [
        { name: this.selectedSensor.nombre, data: hist.map(p => ({ x: p.x, y: p.y })) },
      ]

      this.calcularEstadisticas(token)
      this.ultimaActualizacion = moment().format('HH:mm:ss')

      this.$nextTick(() => {
        if (this.$refs.chart) {
          this.$refs.chart.updateSeries(this.chartSeries, false)
          this.$refs.chart.updateOptions(this.chartOptions, false, true)
        }
        // Redibujar sparklines de todas las cards
        this.sensores.forEach(s => this.dibujarSparkline(s))
      })
    },

    calcularEstadisticas(token) {
      const hist = this.sensorHistorial[token] || []
      if (!hist.length) {
        this.estadisticas = { actual: 0, promedio: 0, mediana: 0, minimo: 0, maximo: 0, muestras: 0 }
        return
      }
      const vals = hist.map(p => p.y)
      this.estadisticas = {
        actual:   vals[vals.length - 1],
        promedio: vals.reduce((a, b) => a + b, 0) / vals.length,
        mediana:  this.calcMediana(vals),
        minimo:   Math.min(...vals),
        maximo:   Math.max(...vals),
        muestras: vals.length,
      }
    },

    calcMediana(arr) {
      if (!arr.length) return 0
      const s = [...arr].sort((a, b) => a - b)
      const m = Math.floor(s.length / 2)
      return s.length % 2 === 0 ? (s[m - 1] + s[m]) / 2 : s[m]
    },

    recortarVentana(token) {
      const hist = this.sensorHistorial[token]
      if (!hist || !hist.length) return
      const limite = hist[hist.length - 1].x - this.ventanaSegundos * 1000
      const filtrado = hist.filter(p => p.x >= limite)
      const final = filtrado.length > this.maxPuntos ? filtrado.slice(-this.maxPuntos) : filtrado
      Vue.set(this.sensorHistorial, token, final)
    },

    // ─── Polling ──────────────────────────────────────────────────────────────
    iniciarPolling() {
      this.detenerPolling()
      const generation = this.pollingGeneration
      const poll = async () => {
        if (!this.monitoring || generation !== this.pollingGeneration) return
        await this.obtenerNuevosDatos()
        if (!this.monitoring || generation !== this.pollingGeneration) return
        this.intervaloPolling = setTimeout(poll, 5000)
      }
      this.intervaloPolling = setTimeout(poll, 5000)
    },

    detenerPolling() {
      this.pollingGeneration += 1
      if (this.intervaloPolling) {
        clearTimeout(this.intervaloPolling)
        this.intervaloPolling = null
      }
      if (this.pollingAbortController) {
        this.pollingAbortController.abort()
        this.pollingAbortController = null
      }
      this.pollingInFlight = false
    },

    toggleMonitoreo() {
      this.monitoring = !this.monitoring
      if (this.monitoring) {
        this.iniciarPolling()
      } else {
        this.detenerPolling()
      }
    },

    limpiarDatos() {
      if (!this.selectedSensor) return
      Vue.set(this.sensorHistorial, this.selectedSensor.token, [])
      this.estadisticas = { actual: 0, promedio: 0, mediana: 0, minimo: 0, maximo: 0, muestras: 0 }
      this.chartSeries = [
        { name: this.selectedSensor.nombre, data: [] },
      ]
      Vue.set(this.liveValues, this.selectedSensor.token, this.selectedSensor.base || 0)
    },

    setVentana(val) {
      this.ventanaSegundos = val
      if (this.selectedSensor) {
        this.cargarDatosIniciales()
      }
    },


    // ─── Sparklines ──────────────────────────────────────────────────────────
    dibujarSparkline(sensor) {
      this.$nextTick(() => {
        const canvas = document.getElementById(`spark-${sensor.token}`)
        if (!canvas) return
        const hist = this.sensorHistorial[sensor.token] || []
        if (hist.length < 2) return
        const W = canvas.offsetWidth || 160
        const H = 36
        canvas.width  = W
        canvas.height = H
        const ctx = canvas.getContext('2d')
        ctx.clearRect(0, 0, W, H)
        const vals = hist.map(p => p.y)
        const mn  = Math.min(...vals)
        const mx  = Math.max(...vals)
        const rng = mx - mn || 1
        const xs  = i => (i / (vals.length - 1)) * W
        const ys  = v => H - 4 - ((v - mn) / rng) * (H - 8)
        ctx.beginPath()
        ctx.moveTo(xs(0), ys(vals[0]))
        vals.forEach((v, i) => { if (i > 0) ctx.lineTo(xs(i), ys(v)) })
        ctx.strokeStyle = sensor.color
        ctx.lineWidth = 1.8
        ctx.lineJoin  = 'round'
        ctx.stroke()
        ctx.lineTo(W, H); ctx.lineTo(0, H); ctx.closePath()
        ctx.fillStyle = sensor.color + '28'
        ctx.fill()
      })
    },

    // ─── Helpers UI ──────────────────────────────────────────────────────────
    getStatusColor(estado) {
      const map = { Corriendo: 'green darken-2', Paro: 'red darken-2', Calibración: 'orange darken-2', Standby: 'orange darken-1' }
      return map[estado] || 'grey'
    },

    sensorTagLabel(sensor) {
      if (!sensor) return ''
      return sensor.tagKey
        ? this.$t(`Reports.RealtimeSensors.categories.${sensor.tagKey}`)
        : (sensor.tag || '')
    },
    getTrend(sensor) {
      const hist = this.sensorHistorial[sensor.token] || []
      if (hist.length < 5) return { icon: 'mdi-minus', color: '#94a3b8', value: '—' }
      const last = hist.slice(-5).map(p => p.y)
      const slope = last[4] - last[0]
      return slope > 0
        ? { icon: 'mdi-trending-up',   color: '#dc2626', value: '+' + Math.abs(slope).toFixed(2) }
        : { icon: 'mdi-trending-down', color: '#16a34a', value: '−' + Math.abs(slope).toFixed(2) }
    },

    tokenToNombre(token) {
      return token
        .replace(/_/g, ' ')
        .replace(/([A-Z]{2,})(\d+)/g, '$1 $2')
        .toLowerCase()
        .replace(/\b\w/g, l => l.toUpperCase())
    },

  },
}
</script>

<style scoped>
/* ── Header aside (language toggle above the step chip) ──────── */
.monitor-header__aside {
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 10px;
}

.monitor-header__aside-chips {
  display: flex;
  align-items: center;
}

/* ── Language toggle ─────────────────────────────────────────── */
.str-language-control {
  align-items: center;
  background: #ffffff;
  border: 1px solid rgba(25, 118, 210, 0.35);
  border-radius: 12px;
  box-shadow: 0 4px 14px rgba(25, 118, 210, 0.14);
  display: inline-flex;
  padding: 6px 8px;
}

.str-language-toggle {
  border-radius: 10px;
  overflow: hidden;
  box-shadow: inset 0 0 0 1px rgba(25, 118, 210, 0.12);
}

.str-language-toggle ::v-deep .v-btn {
  border-radius: 0 !important;
  border: none !important;
  margin: 0 !important;
}

.str-language-btn {
  background: #eef4fd !important;
  color: #1565c0 !important;
  font-weight: 800;
  letter-spacing: .4px;
  min-width: 48px !important;
  transition: all .2s ease;
}

.str-language-btn--active {
  background: linear-gradient(135deg, #1e88e5 0%, #1565c0 100%) !important;
  color: #ffffff !important;
}

.monitor-shell {
  position: relative;
  overflow: hidden;
}

.monitor-intro-overlay {
  position: fixed;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  background: radial-gradient(circle at center, rgba(255, 255, 255, 0.88) 0%, rgba(244, 246, 249, 0.76) 36%, rgba(244, 246, 249, 0) 70%);
  backdrop-filter: blur(2px);
  z-index: 30;
  pointer-events: none;
}

.monitor-intro-icon-wrap {
  position: relative;
  z-index: 3;
  animation: intro-icon-pop 1.05s cubic-bezier(.2, .9, .2, 1) both;
}

.monitor-intro-icon {
  box-shadow: 0 22px 58px rgba(15, 118, 110, 0.28);
}

.monitor-intro-ring {
  position: absolute;
  border-radius: 999px;
  border: 2px solid rgba(15, 118, 110, 0.22);
  animation: intro-ring-expand 1.05s ease-out both;
}

.monitor-intro-ring--outer {
  width: 84px;
  height: 84px;
}

.monitor-intro-ring--inner {
  width: 84px;
  height: 84px;
  animation-delay: .12s;
}

.monitor-intro-burst {
  position: absolute;
  width: 110px;
  height: 110px;
  border-radius: 999px;
  background: radial-gradient(circle, rgba(15, 118, 110, 0.22) 0%, rgba(15, 118, 110, 0.12) 34%, rgba(15, 118, 110, 0) 72%);
  animation: intro-burst-expand 1s ease-out both;
}

.section-banner {
  position: relative;
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 14px 16px;
  border: 1px solid rgba(148, 163, 184, 0.2);
  border-radius: 18px;
  overflow: hidden;
  box-shadow: 0 14px 35px rgba(15, 23, 42, 0.06);
}

.section-banner::before {
  content: "";
  position: absolute;
  inset: 0;
  background: linear-gradient(115deg, transparent 25%, rgba(255, 255, 255, 0.55) 45%, transparent 65%);
  transform: translateX(-130%);
  animation: header-sheen 1.6s ease .15s both;
  pointer-events: none;
}

.section-banner--blue {
  background: linear-gradient(135deg, #ffffff 0%, #f4f9ff 60%, #e9f3ff 100%);
}

.section-banner--slate {
  background: linear-gradient(135deg, #ffffff 0%, #f8fbff 58%, #eef4fb 100%);
}

.section-banner--violet {
  background: linear-gradient(135deg, #ffffff 0%, #faf7ff 58%, #f2ebff 100%);
}

.section-banner__icon {
  position: relative;
  z-index: 1;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 42px;
  height: 42px;
  border-radius: 14px;
  background: rgba(255, 255, 255, 0.7);
  box-shadow: 0 10px 24px rgba(37, 99, 235, 0.12);
}

.section-banner__title,
.section-banner span.text-subtitle-2 {
  position: relative;
  z-index: 1;
  color: #0f172a;
  letter-spacing: .01em;
}

.section-banner__chip {
  position: relative;
  z-index: 1;
  margin-left: auto;
  border-color: rgba(25, 118, 210, 0.24) !important;
  background: rgba(255, 255, 255, 0.72) !important;
}

.monitor-header-card {
  overflow: hidden;
  border-radius: 18px !important;
  border-color: #dbe7f3 !important;
  box-shadow: 0 14px 36px rgba(15, 23, 42, 0.06) !important;
}

.monitor-header {
  position: relative;
  background:
    radial-gradient(circle at top left, rgba(15, 118, 110, 0.18), transparent 32%),
    linear-gradient(135deg, #ffffff 0%, #f8fcfb 55%, #eef8f6 100%);
}

.monitor-header::after {
  content: "";
  position: absolute;
  inset: 0;
  background: linear-gradient(115deg, transparent 22%, rgba(255, 255, 255, 0.6) 44%, transparent 63%);
  transform: translateX(-135%);
  animation: header-sheen 1.35s ease .2s both;
  pointer-events: none;
}

.monitor-header__avatar {
  box-shadow: 0 10px 22px rgba(15, 118, 110, 0.24);
  animation: header-bob .8s ease-out;
}

.monitor-header__copy {
  min-width: 0;
}

.monitor-header__text {
  min-width: 0;
}

.monitor-header__kicker {
  display: inline-flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 6px;
  font-size: 11px;
  font-weight: 700;
  letter-spacing: .12em;
  color: #334155;
  text-transform: uppercase;
}

.monitor-header__kicker-line {
  width: 34px;
  height: 2px;
  border-radius: 999px;
  background: linear-gradient(90deg, #0f172a 0%, #0f766e 100%);
  box-shadow: 0 4px 14px rgba(15, 118, 110, 0.18);
}

.monitor-header__title {
  display: flex;
  flex-wrap: wrap;
  align-items: baseline;
  gap: 10px;
  margin-bottom: 2px;
  color: #0f172a;
  text-shadow: 0 10px 26px rgba(15, 118, 110, 0.12);
  transform-origin: left center;
  animation: title-pop .72s cubic-bezier(.18, .88, .2, 1);
}

.monitor-header__title-main {
  font-size: clamp(1.8rem, 3.2vw, 2.45rem);
  line-height: .95;
  font-weight: 900;
  letter-spacing: -.05em;
  color: #0f172a;
  text-shadow: 0 10px 20px rgba(15, 118, 110, 0.1);
}

.monitor-header__title-ghost {
  font-size: clamp(1rem, 1.6vw, 1.2rem);
  font-weight: 700;
  letter-spacing: .18em;
  text-transform: uppercase;
  color: #0f766e;
  text-shadow: none;
}

.monitor-header__title-accent {
  width: 84px;
  max-width: 100%;
  height: 4px;
  margin: 7px 0 8px;
  border-radius: 999px;
  background: linear-gradient(90deg, #0f766e 0%, #14b8a6 55%, rgba(20, 184, 166, 0) 100%);
  box-shadow: 0 8px 18px rgba(15, 118, 110, 0.2);
  transform-origin: left center;
  animation: accent-grow .78s cubic-bezier(.2, .8, .2, 1);
}

.monitor-header__subtitle {
  color: #475569 !important;
  animation: subtitle-rise .82s cubic-bezier(.2, .8, .2, 1);
  font-size: 13px !important;
}

.monitor-status-chip {
  box-shadow: 0 10px 24px rgba(22, 163, 74, 0.18);
}

.monitor-step-chip {
  border-color: rgba(37, 99, 235, 0.22) !important;
  background: rgba(255, 255, 255, 0.78) !important;
  color: #0f172a !important;
  font-weight: 700;
}

.monitor-steps-card {
  position: relative;
  overflow: hidden;
  border-radius: 22px !important;
  border-color: #e5ebf2 !important;
  box-shadow: none !important;
  background: transparent;
}

.monitor-steps-card::after {
  content: "";
  position: absolute;
  inset: 0;
  border-radius: 22px;
  padding: 2px;
  background: linear-gradient(90deg, rgba(14, 116, 110, 0) 0%, rgba(14, 116, 110, 0) 25%, rgba(14, 116, 110, 0.95) 50%, rgba(15, 118, 110, 0.88) 75%, rgba(14, 116, 110, 0) 100%);
  background-size: 220% 100%;
  opacity: 0;
  pointer-events: none;
  -webkit-mask: linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0);
  -webkit-mask-composite: xor;
  mask: linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0);
  mask-composite: exclude;
}

.monitor-steps-card--complete {
  border-color: rgba(14, 116, 110, 0.35) !important;
  box-shadow: 0 0 0 1px rgba(14, 116, 110, 0.08), 0 14px 34px rgba(14, 116, 110, 0.16) !important;
}

.monitor-steps-card--complete::after {
  opacity: 1;
  animation: stepsOutlineSweep .95s cubic-bezier(.2, .8, .2, 1) 1 forwards;
}

.monitor-step-btn {
  min-height: 164px !important;
  padding: 14px 18px 16px !important;
  border-radius: 24px !important;
  background: linear-gradient(180deg, #ffffff 0%, #f7f9fc 100%) !important;
  border: 1.5px solid rgba(203, 213, 225, 0.88) !important;
  color: #16324f !important;
  box-shadow: 0 10px 28px rgba(15, 23, 42, 0.05);
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 10px;
  transition: transform .22s ease, box-shadow .22s ease, background-color .22s ease, border-color .22s ease !important;
}

.monitor-step-btn:hover {
  transform: translateY(-2px);
  box-shadow: 0 14px 28px rgba(15, 118, 110, 0.16);
}

.monitor-step-btn.v-btn--text {
  background: linear-gradient(180deg, #ffffff 0%, #f5f8fb 100%) !important;
}

.monitor-step-btn.v-btn--outlined {
  background: linear-gradient(180deg, #ffffff 0%, #f5f8fb 100%) !important;
}

.monitor-step-btn.v-btn--has-bg {
  box-shadow: 0 14px 30px rgba(15, 118, 110, 0.18);
}

.monitor-step-btn--state-idle {
  opacity: .94;
}

.monitor-step-btn--state-active {
  box-shadow: 0 18px 34px rgba(15, 118, 110, 0.14);
}

.monitor-step-btn--state-visited {
  box-shadow: 0 14px 26px rgba(15, 23, 42, 0.06);
}

.monitor-step-btn--complete {
  background: linear-gradient(180deg, #ffffff 0%, #eefaf8 100%) !important;
  border-color: rgba(15, 118, 110, 0.34) !important;
  animation: stepCompleteFlash .7s cubic-bezier(.2, .8, .2, 1) 1;
}

.monitor-step-avatar {
  margin-right: 0 !important;
  width: 52px !important;
  min-width: 52px !important;
  height: 52px !important;
  box-shadow: none;
  border: 1px solid #dbe7f3;
  background: #fff !important;
}

.monitor-step-avatar span {
  display: none;
}

.monitor-step-avatar-icon {
  font-size: 24px !important;
}

.monitor-step-avatar--state-idle {
  border-color: #d5dee9;
}

.monitor-step-avatar--state-active {
  border-color: rgba(37, 99, 235, 0.35);
  box-shadow: 0 8px 20px rgba(37, 99, 235, 0.14);
}

.monitor-step-avatar--state-visited {
  border-color: rgba(14, 116, 144, 0.3);
  box-shadow: 0 8px 18px rgba(14, 116, 144, 0.1);
}

.monitor-step-avatar--complete {
  border-color: rgba(15, 118, 110, 0.38);
  box-shadow: 0 0 0 5px rgba(15, 118, 110, 0.1), 0 10px 24px rgba(15, 118, 110, 0.14);
  animation: stepAvatarComplete .7s cubic-bezier(.2, .8, .2, 1) 1;
}

.monitor-step-card-top {
  display: none;
}

.monitor-step-copy {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-width: 0;
  width: 100%;
  text-align: center;
  min-height: 92px;
}

.monitor-step-label {
  color: #1e293b;
  font-size: 14px !important;
  line-height: 1.15;
  margin-bottom: 6px;
  justify-content: center;
  max-width: 100%;
}

.monitor-step-label-icon {
  display: none !important;
}

.monitor-step-value {
  color: #64748b;
  opacity: 1;
  font-weight: 500;
  max-width: 100% !important;
  white-space: normal;
  overflow: visible;
  text-overflow: unset;
  line-height: 1.25;
  display: block;
}

.monitor-step-number {
  font-size: 56px;
  line-height: 1;
  font-weight: 900;
  letter-spacing: -.05em;
  text-align: center;
  margin: 6px 0 8px;
}

.monitor-step-chevron {
  font-size: 26px !important;
  color: #c5d1de !important;
}

.monitor-step-chevron--pulse {
  animation: chevronPulse 1.05s ease-in-out infinite;
}

.monitor-panel-card {
  overflow: hidden;
  border-radius: 28px !important;
  border-color: rgba(203, 213, 225, 0.9) !important;
  box-shadow: 0 28px 60px rgba(15, 23, 42, 0.08) !important;
  background: #ffffff;
}

.monitor-panel-hero {
  position: relative;
  overflow: hidden;
  border-top: 3px solid #10b981;
  border-bottom: 1px solid rgba(226, 232, 240, 0.9);
  background: #ffffff;
}

.monitor-panel-hero__glow {
  display: none;
}

.monitor-panel-hero__content {
  position: relative;
  z-index: 1;
  padding: 26px 28px 24px;
}

.monitor-summary-card {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  padding: 2px 0 0;
}

.monitor-summary-card__main {
  display: flex;
  align-items: center;
  gap: 16px;
  flex: 1 1 420px;
  min-width: 0;
}

.monitor-summary-card__avatar {
  flex-shrink: 0;
  box-shadow: 0 16px 34px rgba(15, 118, 110, 0.14);
  border: 1px solid rgba(15, 118, 110, 0.14);
}

.monitor-summary-card__copy {
  min-width: 0;
}

.monitor-summary-card__eyebrow {
  font-size: 11px;
  font-weight: 800;
  text-transform: uppercase;
  letter-spacing: .18em;
  color: #0f766e;
  margin-bottom: 4px;
}

.monitor-summary-card__title {
  font-size: clamp(1.3rem, 2vw, 1.75rem);
  line-height: 1.02;
  font-weight: 900;
  letter-spacing: -.04em;
  color: #0f172a;
}

.monitor-summary-card__meta {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 8px;
  margin-top: 8px;
  color: #64748b;
  font-size: 12px;
  font-weight: 600;
}

.monitor-summary-card__dot {
  color: #94a3b8;
  font-size: 12px;
}

.monitor-summary-card__actions {
  display: flex;
  align-items: center;
  gap: 12px;
}

.monitor-summary-card__clear {
  border: 1px solid rgba(203, 213, 225, 0.84) !important;
  background: rgba(255,255,255,0.86);
  color: #475569 !important;
}

.monitor-live-pill {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 8px 16px;
  border-radius: 999px;
  border: 1px solid rgba(16, 185, 129, 0.28);
  background: linear-gradient(180deg, rgba(236, 253, 245, 0.96) 0%, rgba(220, 252, 231, 0.92) 100%);
  color: #047857;
  font-size: 12px;
  font-weight: 800;
  letter-spacing: .12em;
  text-transform: uppercase;
}

.monitor-live-pill__dot {
  width: 8px;
  height: 8px;
  border-radius: 999px;
  background: #34d399;
  box-shadow: 0 0 0 4px rgba(52, 211, 153, 0.18);
}

.monitor-overview-grid {
  margin-top: 18px;
}

.monitor-highlight-card {
  height: 100%;
  border-radius: 22px;
  padding: 18px 18px 16px;
  background: linear-gradient(180deg, rgba(255,255,255,0.92) 0%, rgba(255,255,255,0.78) 100%);
  border: 1px solid rgba(203, 213, 225, 0.75);
  box-shadow: inset 0 1px 0 rgba(255,255,255,0.8);
}

.monitor-highlight-card--current {
  border-left: 4px solid #3b82f6;
}

.monitor-highlight-card--range {
  border-left: 3px solid rgba(148, 163, 184, 0.4);
}

.monitor-highlight-card--state {
  border-left: 4px solid #10b981;
}

.monitor-highlight-card--alert {
  border-left-color: #ef4444;
}

.monitor-highlight-card__label {
  font-size: 11px;
  font-weight: 800;
  text-transform: uppercase;
  letter-spacing: .16em;
  color: #64748b;
  margin-bottom: 10px;
}

.monitor-highlight-card__value {
  font-size: clamp(1.8rem, 2.4vw, 2.35rem);
  line-height: 1;
  font-weight: 900;
  letter-spacing: -.06em;
  font-family: monospace;
}

.monitor-highlight-card__subvalue {
  font-size: 1rem;
  line-height: 1.25;
  font-weight: 800;
  color: #0f172a;
}

.monitor-highlight-card__meta {
  margin-top: 10px;
  color: #64748b;
  font-size: 12px;
  font-weight: 600;
}

.monitor-panel-body {
  background:
    linear-gradient(180deg, rgba(255,255,255,0.65) 0%, rgba(248,251,253,0.96) 100%);
}

.monitor-controls-wrap {
  display: grid;
  grid-template-columns: auto 1fr auto;
  align-items: center;
  gap: 16px;
  margin-top: 14px;
  border: 1px solid rgba(226, 232, 240, 0.9);
  border-radius: 22px;
  padding: 14px 18px;
  background: linear-gradient(180deg, rgba(255,255,255,0.96) 0%, rgba(248,250,252,0.92) 100%);
}


.monitor-controls-label {
  font-size: 11px;
  font-weight: 800;
  text-transform: uppercase;
  letter-spacing: .16em;
  color: #475569;
}

.monitor-controls-track {
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 8px;
}


.monitor-window-pill {
  appearance: none;
  border: 0;
  border-radius: 999px;
  min-height: 12px;
  padding: 10px 12px;
  background: #e2e8f0;
  color: #64748b;
  font-size: 12px;
  font-weight: 800;
  letter-spacing: .02em;
  transition: all .2s ease;
  cursor: pointer;
}

.monitor-window-pill--active {
  background: linear-gradient(90deg, #10b981 0%, #34d399 100%);
  color: #ffffff;
  box-shadow: 0 10px 22px rgba(16, 185, 129, 0.24);
}


.monitor-controls-trigger {
  min-width: 110px;
  font-weight: 800 !important;
  letter-spacing: .02em;
}

.monitor-chart-shell {
  border-radius: 26px;
  overflow: hidden;
  border: 1px solid rgba(222, 211, 181, 0.9);
  background:
    radial-gradient(circle at top left, rgba(255, 220, 138, 0.14), transparent 26%),
    linear-gradient(180deg, #fffdf8 0%, #fff7e8 100%);
  box-shadow: inset 0 1px 0 rgba(255,255,255,0.92), 0 16px 34px rgba(120, 87, 32, 0.08);
}

.monitor-chart-shell__header {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 18px 20px;
  border-bottom: 1px solid rgba(232, 220, 188, 0.95);
  background: linear-gradient(180deg, rgba(255,255,255,0.96) 0%, rgba(255,249,237,0.98) 100%);
}

.monitor-chart-shell__title {
  font-size: 1rem;
  font-weight: 800;
  color: #0f172a;
}

.monitor-chart-shell__subtitle {
  margin-top: 4px;
  font-size: 12px;
  font-weight: 600;
  color: #64748b;
}

.monitor-chart-shell__status {
  display: inline-flex;
  align-items: center;
  padding: 9px 12px;
  border-radius: 999px;
  background: rgba(255,255,255,0.88);
  border: 1px solid rgba(203, 213, 225, 0.85);
  color: #475569;
  font-size: 12px;
  font-weight: 700;
}

.monitor-chart-shell__body {
  padding: 14px 16px 18px;
  background: linear-gradient(180deg, rgba(255,250,240,0.68) 0%, rgba(255,247,233,0.96) 100%);
}

.monitor-chart-shell__empty {
  height: 320px;
}

.monitor-stat-card {
  border-radius: 20px !important;
  border-color: rgba(203, 213, 225, 0.78) !important;
  background: #ffffff;
  box-shadow: 0 10px 24px rgba(15, 23, 42, 0.04);
}

.monitor-stat-card__head {
  margin-bottom: 12px;
}

.monitor-stat-card__icon-wrap {
  width: 34px;
  min-width: 34px;
  height: 34px;
  border-radius: 10px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border: 1px solid rgba(255,255,255,0.92);
  box-shadow: inset 0 1px 0 rgba(255,255,255,0.92);
}

.monitor-stat-card__copy {
  min-width: 0;
}

.monitor-stat-card__value {
  margin-top: 6px;
  font-size: 14px;
  font-family: monospace;
  font-variant-numeric: tabular-nums;
  font-weight: 1000;
  color: #0f172a;
  line-height: 1.1;
  letter-spacing: -.03em;
}

.monitor-stat-card__label {
  font-size: 11px;
  color: #94a3b8;
  font-weight: 800;
  text-transform: uppercase;
  letter-spacing: .14em;
}

.monitor-stat-card__meta {
  margin-top: 5px;
  font-size: 11px;
  color: #94a3b8;
  font-weight: 600;
  line-height: 1.25;
}

.monitor-stat-card__badge {
  margin-top: 12px;
  display: inline-flex;
  align-items: center;
  padding: 4px 10px;
  border-radius: 999px;
  font-size: 11px;
  font-weight: 800;
  letter-spacing: .04em;
  box-shadow: 0 10px 18px rgba(15, 23, 42, 0.08);
}

.monitor-info-chip {
  background: rgba(255,255,255,0.86) !important;
  border-color: rgba(148, 163, 184, 0.32) !important;
}

@media (max-width: 960px) {
  .monitor-panel-hero__content {
    padding: 22px 20px;
  }

  .monitor-summary-card {
    align-items: flex-start;
  }

  .monitor-summary-card__actions {
    width: 100%;
    justify-content: flex-start;
    margin-top: 16px;
  }

  .monitor-controls-wrap {
    grid-template-columns: 1fr;
  }

  .monitor-chart-shell__header {
    padding: 16px 16px 14px;
  }

  .monitor-steps-card {
    border-radius: 18px !important;
  }

  .monitor-step-btn {
    min-height: 150px !important;
    padding: 14px 14px 16px !important;
    gap: 8px;
  }

  .monitor-step-avatar {
    width: 48px !important;
    min-width: 48px !important;
    height: 48px !important;
  }

  .monitor-step-label {
    font-size: 13px !important;
  }

  .monitor-step-label-icon {
    font-size: 20px !important;
  }

  .monitor-step-number {
    font-size: 48px;
  }

  .monitor-step-value {
    font-size: 11px !important;
    max-width: 100% !important;
  }

  .monitor-step-chevron {
    font-size: 24px !important;
    margin-left: 4px !important;
    margin-right: 4px !important;
  }

  .sensor-card__poster {
    grid-template-columns: 1fr;
  }

  .sensor-card__title {
    font-size: 22px;
  }
}

@media (max-width: 600px) {
  .monitor-summary-card__main {
    align-items: flex-start;
  }

  .monitor-controls-track {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }

  .monitor-controls-wrap {
    padding: 14px;
  }

  .monitor-chart-shell__body {
    padding: 10px 8px 14px;
  }

  .monitor-steps-row {
    row-gap: 8px;
  }

  .monitor-step-btn {
    min-height: 138px !important;
    border-radius: 18px !important;
    padding: 12px !important;
  }

  .monitor-step-copy {
    max-width: 100%;
    min-height: 84px;
  }

  .monitor-step-label {
    font-size: 12px !important;
  }

  .monitor-step-label-icon {
    font-size: 18px !important;
    margin-right: 6px !important;
  }

  .monitor-step-number {
    font-size: 40px;
  }

  .monitor-step-value {
    font-size: 10px !important;
    max-width: 100% !important;
  }

  .monitor-step-chevron {
    font-size: 20px !important;
  }

  .sensor-card__title {
    font-size: 17px;
  }

  .sensor-card__header,
  .sensor-card__section,
  .sensor-card__footer {
    padding: 16px;
  }

  .sensor-card__shell {
    min-height: 400px;
  }

  .sensor-card__header {
    flex-direction: column;
  }

  .sensor-card__header-main {
    width: 100%;
  }

  .sensor-card__reading-value {
    font-size: 42px;
  }

  .sensor-card__section--meta {
    justify-content: flex-start;
  }

  .sensor-card__footer {
    flex-direction: column;
    align-items: flex-start;
  }
}

.area-card {
  cursor: pointer;
  transition: border-color .24s, box-shadow .24s, transform .24s;
  border-radius: 12px !important;
  animation: premium-card-rise .65s cubic-bezier(.2, .8, .2, 1) both;
}
.area-card:hover {
  border-color: #90caf9 !important;
  box-shadow: 0 18px 34px rgba(25,118,210,.14) !important;
  transform: translateY(-6px) scale(1.01);
}
.selected-card {
  border-color: color-mix(in srgb, var(--sensor-accent) 52%, #ffffff 48%) !important;
  background: linear-gradient(180deg, #ffffff 0%, color-mix(in srgb, var(--sensor-soft) 28%, #eff6ff 72%) 100%) !important;
  box-shadow:
    0 0 0 3px color-mix(in srgb, var(--sensor-accent) 14%, transparent),
    0 18px 34px color-mix(in srgb, var(--sensor-accent) 14%, rgba(15,23,42,0.08)) !important;
}
.sensor-group-card {
  overflow: hidden;
  border-radius: 22px !important;
  border-color: color-mix(in srgb, var(--sensor-accent) 22%, rgba(148,163,184,0.22)) !important;
  animation: premium-card-rise .65s cubic-bezier(.2, .8, .2, 1) both;
}
.sensor-group-card::before {
  background: linear-gradient(
    90deg,
    color-mix(in srgb, var(--sensor-accent) 76%, #ffffff 24%),
    rgba(255,255,255,0.88),
    color-mix(in srgb, var(--sensor-accent) 72%, #ffffff 28%)
  );
}
.sensor-group-card__header {
  position: relative;
  z-index: 1;
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  padding: 20px 22px;
  border-bottom: 1px solid rgba(226, 232, 240, 0.88);
  background:
    radial-gradient(circle at top right, color-mix(in srgb, var(--sensor-accent) 10%, transparent) 0%, transparent 28%),
    linear-gradient(180deg, #ffffff 0%, color-mix(in srgb, var(--sensor-soft) 28%, #f8fbff 72%) 100%);
}
.sensor-group-card__main {
  display: flex;
  align-items: center;
  gap: 14px;
  min-width: 0;
}
.sensor-group-card__icon-box {
  width: 52px;
  min-width: 52px;
  height: 52px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border-radius: 15px;
  border: 1px solid color-mix(in srgb, var(--sensor-accent) 28%, #ffffff 72%);
  background: linear-gradient(180deg, color-mix(in srgb, var(--sensor-soft) 84%, #ffffff 16%) 0%, #ffffff 100%);
  box-shadow: inset 0 1px 0 rgba(255,255,255,0.9), 0 12px 24px color-mix(in srgb, var(--sensor-accent) 10%, transparent);
}
.sensor-group-card__copy {
  min-width: 0;
}
.sensor-group-card__eyebrow {
  margin-bottom: 4px;
  font-size: 11px;
  font-weight: 900;
  letter-spacing: .16em;
  text-transform: uppercase;
  color: color-mix(in srgb, var(--sensor-accent) 72%, #475569 28%);
}
.sensor-group-card__title {
  font-size: 20px;
  line-height: 1.1;
  font-weight: 900;
  color: #0f172a;
}
.sensor-group-card__subtitle {
  margin-top: 6px;
  color: #64748b;
  font-size: 11px;
  font-weight: 800;
  letter-spacing: .14em;
  text-transform: uppercase;
}
.sensor-group-card__chip {
  background: rgba(255,255,255,0.78) !important;
  border-color: color-mix(in srgb, var(--sensor-accent) 22%, rgba(148,163,184,0.32)) !important;
  color: #334155 !important;
  font-weight: 800;
}
.sensor-group-card__variables {
  position: relative;
  z-index: 1;
  padding: 16px;
}
.sensor-group-card__empty {
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 88px;
  border: 1px dashed rgba(148, 163, 184, 0.38);
  border-radius: 14px;
  color: #64748b;
  font-size: 13px;
  font-weight: 700;
  background: rgba(248, 250, 252, 0.72);
}
.sensor-card {
  cursor: pointer;
  transition: border-color .24s, box-shadow .24s, transform .24s;
  border-radius: 24px !important;
  min-height: 100%;
  overflow: hidden;
  background:
    linear-gradient(180deg, #ffffff 0%, color-mix(in srgb, var(--sensor-soft) 18%, #f8fbff 82%) 100%);
  border-color: color-mix(in srgb, var(--sensor-accent) 20%, rgba(148,163,184,0.18)) !important;
}
.sensor-card--alert {
  box-shadow: 0 18px 34px rgba(15, 23, 42, 0.08) !important;
}
.sensor-card:hover {
  box-shadow: 0 18px 34px color-mix(in srgb, var(--sensor-accent) 16%, rgba(15,23,42,0.08)) !important;
  transform: translateY(-6px) scale(1.01);
}
.sensor-card.premium-card::before {
  background: linear-gradient(
    90deg,
    color-mix(in srgb, var(--sensor-accent) 98%, #ffffff 2%) 0%,
    color-mix(in srgb, var(--sensor-accent) 82%, #ffffff 18%) 22%,
    rgba(255,255,255,0.98) 50%,
    color-mix(in srgb, var(--sensor-accent) 84%, #ffffff 16%) 78%,
    color-mix(in srgb, var(--sensor-accent) 100%, #ffffff 0%) 100%
  );
  background-size: 220% 100%;
  box-shadow: 0 0 10px color-mix(in srgb, var(--sensor-accent) 22%, transparent);
  animation: sensorToplineLoad 2.2s linear infinite;
}
.sensor-card__body {
  height: 100%;
}
.sensor-card__shell {
  min-height: 430px;
  border-radius: 24px;
  background:
    radial-gradient(circle at top right, color-mix(in srgb, var(--sensor-accent) 12%, transparent) 0%, transparent 28%),
    linear-gradient(180deg, #ffffff 0%, #f8fbff 100%);
}
.sensor-card__header,
.sensor-card__section,
.sensor-card__footer {
  padding: 20px 22px;
}
.sensor-card__header,
.sensor-card__section,
.sensor-card__footer {
  border-bottom: 1px solid rgba(226, 232, 240, 0.9);
}
.sensor-card__footer {
  border-bottom: 0;
  padding-top: 16px;
  padding-bottom: 18px;
}
.sensor-card__header-main {
  display: flex;
  align-items: flex-start;
  gap: 14px;
  min-width: 0;
}
.sensor-card__header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 14px;
}
.sensor-card__icon-box {
  width: 48px;
  min-width: 48px;
  height: 48px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border-radius: 14px;
  border: 1px solid color-mix(in srgb, var(--sensor-accent) 34%, #ffffff 66%);
  background: linear-gradient(
    180deg,
    color-mix(in srgb, var(--sensor-soft) 82%, #ffffff 18%) 0%,
    color-mix(in srgb, var(--sensor-soft) 38%, #ffffff 62%) 100%
  );
  box-shadow: inset 0 1px 0 rgba(255,255,255,0.9);
}
.sensor-card__title {
  font-size: 18px;
  line-height: 1.1;
  font-weight: 900;
  color: #1f2937;
}
.sensor-card__subtitle {
  margin-top: 8px;
  font-size: 11px;
  line-height: 1.4;
  letter-spacing: .18em;
  text-transform: uppercase;
  color: #a3afc2;
}
.sensor-card__copy {
  min-width: 0;
}
.sensor-card__subtitle-dot {
  margin: 0 8px;
}
.sensor-card__metric {
  margin-top: 0;
}
.sensor-card__metric-label {
  margin-bottom: 10px;
  font-size: 11px;
  font-weight: 800;
  letter-spacing: .2em;
  text-transform: uppercase;
  color: #b0bccd;
}
.sensor-card__reading-row {
  display: flex;
  align-items: flex-start;
  gap: 6px;
}
.sensor-card__reading-value {
  font-size: 56px;
  line-height: .88;
  font-weight: 1000;
  letter-spacing: -.055em;
  font-family: "Segoe UI", "Trebuchet MS", "Arial", sans-serif;
  font-variant-numeric: lining-nums tabular-nums;
  background: linear-gradient(
    180deg,
    color-mix(in srgb, #ffffff 22%, var(--sensor-accent) 78%) 0%,
    color-mix(in srgb, #ffd7b0 30%, var(--sensor-accent) 70%) 16%,
    color-mix(in srgb, #ffffff 8%, var(--sensor-accent) 92%) 45%,
    color-mix(in srgb, #c2410c 18%, var(--sensor-accent) 82%) 100%
  );
  -webkit-background-clip: text;
  background-clip: text;
  -webkit-text-fill-color: transparent;
  color: transparent;
  filter: drop-shadow(0 8px 16px color-mix(in srgb, var(--sensor-accent) 16%, transparent));
}
.sensor-card__reading-unit {
  margin-top: 10px;
  font-size: 20px;
  font-weight: 700;
  letter-spacing: -.02em;
  color: #a3b1c6;
}
.sensor-card__bar {
  position: relative;
  margin-top: 18px;
  height: 24px;
}
.sensor-card__bar-track {
  position: absolute;
  top: 8px;
  left: 0;
  right: 0;
  height: 10px;
  border-radius: 999px;
  background: #e9eef7;
}
.sensor-card__bar-fill {
  position: absolute;
  top: 8px;
  left: 0;
  height: 10px;
  border-radius: 999px;
  background: linear-gradient(
    90deg,
    color-mix(in srgb, #ffffff 18%, var(--sensor-accent) 82%) 0%,
    color-mix(in srgb, #ffd7b0 18%, var(--sensor-accent) 82%) 28%,
    color-mix(in srgb, #ffffff 10%, var(--sensor-accent) 90%) 62%,
    color-mix(in srgb, #c2410c 12%, var(--sensor-accent) 88%) 100%
  );
  box-shadow:
    inset 0 1px 0 rgba(255,255,255,0.35),
    0 3px 8px color-mix(in srgb, var(--sensor-accent) 22%, transparent);
}
.sensor-card__bar-knob {
  position: absolute;
  top: 1px;
  width: 20px;
  height: 20px;
  border-radius: 999px;
  background: radial-gradient(circle at 30% 30%, #fff6ef 0%, color-mix(in srgb, #ffffff 14%, var(--sensor-accent) 86%) 28%, var(--sensor-accent) 100%);
  border: 3px solid #fff;
  box-shadow:
    0 0 0 3px color-mix(in srgb, var(--sensor-accent) 20%, transparent),
    0 6px 14px color-mix(in srgb, var(--sensor-accent) 24%, transparent);
}
.sensor-card__bar-meta {
  margin-top: 10px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  font-size: 11px;
  font-weight: 700;
  color: #c0c9d7;
}
.sensor-card__meta-row {
  display: flex;
  align-items: stretch;
  justify-content: space-between;
  gap: 12px;
}
.sensor-card__section--meta {
  display: flex;
  align-items: center;
  justify-content: flex-start;
}
.sensor-card__meta-item {
  display: flex;
  flex-direction: column;
  min-width: 0;
}
.sensor-card__meta-label {
  margin-bottom: 6px;
  font-size: 11px;
  font-weight: 800;
  letter-spacing: .18em;
  text-transform: uppercase;
  color: #b0bccd;
}
.sensor-card__state {
  font-size: 14px;
  font-weight: 800;
}
.sensor-card__footer {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
}
.sensor-card__timestamp {
  font-size: 11px;
  letter-spacing: .08em;
  color: #b0bccd;
  font-weight: 600;
}
.sensor-card__chip {
  text-transform: uppercase;
  letter-spacing: .16em;
  font-weight: 800;
  box-shadow: 0 8px 20px rgba(15, 23, 42, 0.12);
}
.sensor-card__chip--alert {
  border: 1px solid rgba(248, 113, 113, 0.4);
  background: rgba(254, 242, 242, 0.96) !important;
  box-shadow: 0 8px 18px rgba(220, 38, 38, 0.12);
  animation: sensorAlertPulse 1.15s ease-in-out infinite;
}
.sensor-card__alert-chip {
  box-shadow: 0 10px 24px rgba(220, 38, 38, 0.18);
}
.premium-card {
  position: relative;
  overflow: hidden;
  border: 1px solid rgba(148, 163, 184, 0.18) !important;
  background: linear-gradient(180deg, rgba(255,255,255,.98) 0%, rgba(248,251,255,.94) 100%) !important;
  backdrop-filter: blur(8px);
}

.premium-card::before {
  content: "";
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  height: 3px;
  z-index: 2;
  background: linear-gradient(90deg, rgba(25,118,210,.85), rgba(96,165,250,.2));
}

.premium-card__glow {
  position: absolute;
  inset: 0;
  pointer-events: none;
}

.premium-card .v-card__text {
  position: relative;
  z-index: 1;
}

.premium-card .v-avatar {
  box-shadow: 0 14px 28px rgba(15, 23, 42, 0.12);
}

.premium-card .text-subtitle-2,
.premium-card .text-caption.font-weight-bold {
  color: #0f172a !important;
  font-weight: 800 !important;
  letter-spacing: .01em;
}

.premium-card .text-caption.text--secondary,
.premium-card .text-caption.text--secondary.font-weight-medium {
  color: #64748b !important;
}

.premium-card .v-divider {
  border-color: rgba(148, 163, 184, 0.14) !important;
}

.premium-card--sensor canvas {
  border-radius: 10px;
  background: linear-gradient(180deg, rgba(255,255,255,.6) 0%, rgba(241,245,249,.95) 100%);
}

.premium-card--sensor .font-weight-bold[style*="font-size:26px"] {
  text-shadow: 0 8px 18px rgba(15, 23, 42, 0.08);
}

.premium-card--area .text-subtitle-2::after,
.premium-card--machine .text-subtitle-2::after,
.premium-card--sensor .text-caption.font-weight-bold::after {
  content: "";
  display: block;
  width: 42px;
  height: 3px;
  margin-top: 7px;
  border-radius: 999px;
  background: linear-gradient(90deg, rgba(25,118,210,.95), rgba(96,165,250,.2));
}

.premium-card--sensor .text-caption.font-weight-bold::after {
  width: 34px;
}
@keyframes sensorAlertPulse {
  0% {
    opacity: .68;
    transform: scale(1);
    box-shadow: 0 8px 18px rgba(220, 38, 38, 0.1);
  }
  50% {
    opacity: 1;
    transform: scale(1.03);
    box-shadow: 0 10px 22px rgba(220, 38, 38, 0.22);
  }
  100% {
    opacity: .68;
    transform: scale(1);
    box-shadow: 0 8px 18px rgba(220, 38, 38, 0.1);
  }
}
@keyframes sensorToplineLoad {
  0% {
    background-position: 200% 0;
    opacity: .92;
  }
  50% {
    background-position: 100% 0;
    opacity: 1;
  }
  100% {
    background-position: 0% 0;
    opacity: .92;
  }
}
.fade-slide-enter-active,
.fade-slide-leave-active {
  transition: opacity .25s ease, transform .25s ease;
}
.fade-slide-enter {
  opacity: 0;
  transform: translateY(10px);
}
.fade-slide-leave-to {
  opacity: 0;
  transform: translateY(-6px);
}

.header-swap-enter-active,
.header-swap-leave-active {
  transition: opacity .48s ease, transform .48s cubic-bezier(.2, .8, .2, 1), filter .48s ease;
}

.header-swap-enter {
  opacity: 0;
  filter: blur(8px);
  transform: translateY(24px) scale(.94);
}

.header-swap-leave-to {
  opacity: 0;
  filter: blur(8px);
  transform: translateY(-18px) scale(.96);
}

.monitor-intro-enter-active,
.monitor-intro-leave-active {
  transition: opacity .3s ease;
}

.monitor-intro-enter,
.monitor-intro-leave-to {
  opacity: 0;
}

@keyframes title-pop {
  0% {
    opacity: 0;
    filter: blur(10px);
    transform: translateY(26px) scale(.9);
    letter-spacing: .16em;
  }
  62% {
    opacity: 1;
    filter: blur(0);
    transform: translateY(-3px) scale(1.04);
  }
  100% {
    opacity: 1;
    filter: blur(0);
    transform: translateY(0) scale(1);
    letter-spacing: .04em;
  }
}

@keyframes subtitle-rise {
  0% {
    opacity: 0;
    filter: blur(5px);
    transform: translateY(16px);
  }
  100% {
    opacity: 1;
    filter: blur(0);
    transform: translateY(0);
  }
}

@keyframes accent-grow {
  0% {
    opacity: 0;
    transform: scaleX(.08);
  }
  65% {
    opacity: 1;
    transform: scaleX(1.08);
  }
  100% {
    opacity: 1;
    transform: scaleX(1);
  }
}

@keyframes header-bob {
  0% {
    opacity: 0;
    transform: translateY(-10px) scale(.9);
  }
  70% {
    transform: translateY(2px) scale(1.04);
  }
  100% {
    opacity: 1;
    transform: translateY(0) scale(1);
  }
}

@keyframes header-sheen {
  0% {
    transform: translateX(-135%);
  }
  100% {
    transform: translateX(135%);
  }
}

@keyframes intro-icon-pop {
  0% {
    opacity: 0;
    transform: scale(.3) rotate(-10deg);
  }
  55% {
    opacity: 1;
    transform: scale(1.18) rotate(0deg);
  }
  100% {
    opacity: 0;
    transform: scale(1.95);
  }
}

@keyframes intro-ring-expand {
  0% {
    opacity: 0;
    transform: scale(.35);
  }
  30% {
    opacity: .95;
  }
  100% {
    opacity: 0;
    transform: scale(5.2);
  }
}

@keyframes intro-burst-expand {
  0% {
    opacity: 0;
    transform: scale(.25);
  }
  35% {
    opacity: .9;
  }
  100% {
    opacity: 0;
    transform: scale(4.8);
  }
}

@keyframes premium-card-rise {
  0% {
    opacity: 0;
    transform: translateY(24px) scale(.96);
  }
  100% {
    opacity: 1;
    transform: translateY(0) scale(1);
  }
}

@keyframes stepCompleteFlash {
  0% {
    box-shadow: 0 14px 26px rgba(15, 118, 110, 0.16), inset 0 1px 0 rgba(255,255,255,.34), 0 0 0 0 rgba(15, 118, 110, 0);
    transform: scale(1);
  }
  50% {
    box-shadow: 0 18px 34px rgba(15, 118, 110, 0.22), inset 0 1px 0 rgba(255,255,255,.34), 0 0 0 6px rgba(15, 118, 110, 0.16);
    transform: scale(1.02);
  }
  100% {
    box-shadow: 0 14px 26px rgba(15, 118, 110, 0.16), inset 0 1px 0 rgba(255,255,255,.34), 0 0 0 0 rgba(15, 118, 110, 0);
    transform: scale(1);
  }
}

@keyframes stepAvatarComplete {
  0% {
    box-shadow: 0 8px 18px rgba(15, 23, 42, 0.1);
    transform: scale(1);
  }
  50% {
    box-shadow: 0 0 0 6px rgba(15, 118, 110, 0.14), 0 10px 24px rgba(15, 118, 110, 0.18);
    transform: scale(1.06);
  }
  100% {
    box-shadow: 0 8px 18px rgba(15, 23, 42, 0.1);
    transform: scale(1);
  }
}

@keyframes chevronPulse {
  0% {
    opacity: .45;
    transform: translateX(0);
    color: #fca5a5;
  }
  50% {
    opacity: 1;
    transform: translateX(3px);
    color: #ef4444;
  }
  100% {
    opacity: .45;
    transform: translateX(0);
    color: #fca5a5;
  }
}

@keyframes stepsOutlineSweep {
  0% {
    opacity: 1;
    background-position: 200% 0;
  }
  70% {
    opacity: 1;
    background-position: 20% 0;
  }
  100% {
    opacity: 1;
    background-position: 0 0;
    background: linear-gradient(90deg, rgba(14, 116, 110, 0.96), rgba(15, 118, 110, 0.9));
  }
}

/* ============================================================
   SENSOR CARD — Barra de configuracion de rangos
   ============================================================ */
.sensor-rango-config {
  margin-top: 14px;
  padding: 10px 12px 12px;
  background: #fafbfc;
  border: 1px solid #eef1f6;
  border-radius: 10px;
}

.sensor-rango-config__head {
  display: flex;
  align-items: center;
  gap: 5px;
  font-size: 10.5px;
  font-weight: 800;
  letter-spacing: 0.10em;
  text-transform: uppercase;
  color: #475569;
  margin-bottom: 8px;
}
.sensor-rango-config__head ::v-deep .v-icon { color: #475569 !important; }

.sensor-rango-bar {
  display: flex;
  height: 8px;
  border-radius: 999px;
  overflow: hidden;
}

.sensor-rango-bar__seg--alarma { background: #dc2626; }
.sensor-rango-bar__seg--adv { background: #f59e0b; }
.sensor-rango-bar__seg--normal { background: #16a34a; }

.sensor-rango-bar-axis {
  position: relative;
  height: 18px;
  margin-top: 2px;
}

.sensor-rango-bar-axis__tick {
  position: absolute;
  top: 0;
  transform: translateX(-50%);
  font-size: 9.5px;
  color: #64748b;
  font-weight: 600;
  white-space: nowrap;
}
.sensor-rango-bar-axis__tick::before {
  content: "";
  position: absolute;
  top: -6px;
  left: 50%;
  transform: translateX(-50%);
  width: 1px;
  height: 5px;
  background: #cbd5e1;
}

.sensor-rango-config__legend {
  display: flex;
  gap: 10px;
  justify-content: center;
  margin-top: 6px;
}

.sensor-rango-config__legend-item {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  font-size: 10px;
  font-weight: 700;
  letter-spacing: 0.05em;
  text-transform: uppercase;
  color: #64748b;
}

.sensor-rango-config__legend-item::before {
  content: "";
  width: 8px;
  height: 8px;
  border-radius: 2px;
  display: inline-block;
}
.sensor-rango-config__legend-item--alarma::before { background: #dc2626; }
.sensor-rango-config__legend-item--adv::before { background: #f59e0b; }
.sensor-rango-config__legend-item--normal::before { background: #16a34a; }

/* ============================================================
   MONITOR PANEL — Configuracion de rangos (panel premium)
   ============================================================ */
.rango-monitor {
  position: relative;
  background: #ffffff;
  border: 1px solid #e6ebf2;
  border-radius: 16px;
  padding: 20px 22px;
  box-shadow: 0 1px 3px rgba(15, 39, 78, 0.04);
  overflow: hidden;
}

.rango-monitor::before {
  content: "";
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  height: 3px;
  background: linear-gradient(90deg, #dc2626 0%, #f59e0b 30%, #16a34a 50%, #f59e0b 70%, #dc2626 100%);
}

.rango-monitor__header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 18px;
  margin-bottom: 18px;
  padding-bottom: 14px;
  border-bottom: 1px solid #eef1f6;
}

.rango-monitor__title-row {
  display: flex;
  gap: 14px;
  align-items: flex-start;
  flex: 1;
  min-width: 0;
}

.rango-monitor__icon-box {
  width: 44px;
  height: 44px;
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: linear-gradient(135deg, rgba(245, 158, 11, 0.12) 0%, rgba(22, 163, 74, 0.10) 100%);
  flex-shrink: 0;
}

.rango-monitor__icon-box ::v-deep .v-icon {
  color: #f59e0b !important;
  font-size: 22px !important;
}

.rango-monitor__eyebrow {
  font-size: 11px;
  font-weight: 800;
  letter-spacing: 0.14em;
  color: #94a3b8;
  margin-bottom: 2px;
}

.rango-monitor__title {
  font-size: 17px;
  font-weight: 800;
  color: #0f172a;
  letter-spacing: -0.01em;
  line-height: 1.2;
}

.rango-monitor__subtitle {
  margin-top: 4px;
  font-size: 12.5px;
  color: #64748b;
  line-height: 1.4;
}

.rango-monitor__current {
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  flex-shrink: 0;
  text-align: right;
}

.rango-monitor__current-label {
  font-size: 10px;
  font-weight: 800;
  letter-spacing: 0.12em;
  color: #94a3b8;
}

.rango-monitor__current-value {
  font-size: 24px;
  font-weight: 800;
  letter-spacing: -0.02em;
  line-height: 1.05;
  margin-top: 2px;
}

.rango-monitor__current-zone {
  font-size: 11.5px;
  font-weight: 700;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  margin-top: 4px;
}

/* Legend */
.rango-monitor__legend {
  display: flex;
  justify-content: space-between;
  gap: 14px;
  margin-bottom: 10px;
  flex-wrap: wrap;
}

.rango-monitor__legend-item {
  flex: 1;
  min-width: 110px;
  text-align: center;
}

.rango-monitor__legend-title {
  font-size: 11.5px;
  font-weight: 800;
  letter-spacing: 0.10em;
}

.rango-monitor__legend-range {
  font-size: 11px;
  color: #64748b;
  margin-top: 2px;
  white-space: nowrap;
}

.rango-monitor__legend-item--alarma .rango-monitor__legend-title { color: #dc2626; }
.rango-monitor__legend-item--adv .rango-monitor__legend-title { color: #f59e0b; }
.rango-monitor__legend-item--normal .rango-monitor__legend-title { color: #16a34a; }

/* Bar wrap + marker */
.rango-monitor__bar-wrap {
  position: relative;
  margin: 8px 0 4px;
}

.rango-monitor__bar {
  display: flex;
  height: 14px;
  border-radius: 999px;
  overflow: hidden;
  box-shadow: inset 0 1px 3px rgba(15, 39, 78, 0.08);
}

.rango-monitor__bar-seg--alarma { background: #dc2626; }
.rango-monitor__bar-seg--adv { background: #f59e0b; }
.rango-monitor__bar-seg--normal { background: #16a34a; }

.rango-monitor__marker {
  position: absolute;
  top: -22px;
  transform: translateX(-50%);
  display: flex;
  flex-direction: column;
  align-items: center;
  pointer-events: none;
}

.rango-monitor__marker-value {
  display: inline-block;
  color: #fff;
  font-size: 11px;
  font-weight: 800;
  padding: 3px 8px;
  border-radius: 6px;
  white-space: nowrap;
  letter-spacing: 0.02em;
  box-shadow: 0 4px 10px -4px rgba(15, 39, 78, 0.35);
}

.rango-monitor__marker-pin {
  width: 2px;
  height: 28px;
  margin-top: 2px;
  border-radius: 2px;
}

/* Axis */
.rango-monitor__axis {
  position: relative;
  height: 22px;
  margin-top: 4px;
}

.rango-monitor__axis-tick {
  position: absolute;
  top: 0;
  transform: translateX(-50%);
  font-size: 11px;
  color: #475569;
  font-weight: 700;
  white-space: nowrap;
}

.rango-monitor__axis-tick::before {
  content: "";
  position: absolute;
  top: -8px;
  left: 50%;
  transform: translateX(-50%);
  width: 1px;
  height: 6px;
  background: #cbd5e1;
}

/* Summary */
.rango-monitor__summary {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(150px, 1fr));
  gap: 10px;
  margin-top: 18px;
  padding-top: 16px;
  border-top: 1px solid #eef1f6;
}

.rango-monitor__summary-cell {
  background: #fafbfc;
  border: 1px solid #e6ebf2;
  border-radius: 10px;
  padding: 12px 10px;
  text-align: center;
}

.rango-monitor__summary-label {
  font-size: 10.5px;
  font-weight: 800;
  letter-spacing: 0.08em;
}

.rango-monitor__summary-sub {
  font-size: 11px;
  color: #64748b;
  margin: 3px 0 2px;
}

.rango-monitor__summary-value {
  font-size: 12px;
  font-weight: 800;
  color: #0f172a;
}

.rango-monitor__summary-cell--alarma { border-color: rgba(220, 38, 38, 0.25); }
.rango-monitor__summary-cell--alarma .rango-monitor__summary-label { color: #dc2626; }
.rango-monitor__summary-cell--adv { border-color: rgba(245, 158, 11, 0.28); }
.rango-monitor__summary-cell--adv .rango-monitor__summary-label { color: #b45309; }
.rango-monitor__summary-cell--normal { border-color: rgba(22, 163, 74, 0.25); }
.rango-monitor__summary-cell--normal .rango-monitor__summary-label { color: #16a34a; }

@media (max-width: 960px) {
  .rango-monitor__header { flex-direction: column; }
  .rango-monitor__current { align-items: flex-start; text-align: left; }
  .rango-monitor__legend { gap: 6px; }
  .rango-monitor__legend-item { min-width: 80px; }
}

/* Selected sensor control center */
.monitor-panel-card {
  border: 1px solid rgba(181, 203, 232, 0.72) !important;
  border-radius: 34px !important;
  background: #f8fbff !important;
  box-shadow: 0 32px 80px rgba(67, 99, 151, 0.16) !important;
}

.monitor-panel-hero {
  isolation: isolate;
  border: 0;
  background:
    radial-gradient(circle at 8% 18%, rgba(125, 211, 252, 0.46), transparent 26%),
    radial-gradient(circle at 88% 15%, rgba(216, 180, 254, 0.42), transparent 30%),
    radial-gradient(circle at 70% 95%, rgba(253, 186, 116, 0.28), transparent 34%),
    linear-gradient(135deg, #f3fbff 0%, #f8f5ff 48%, #fff9f2 100%);
}

.monitor-panel-hero__glow {
  display: block;
  position: absolute;
  inset: 0;
  opacity: .55;
  background-image: radial-gradient(rgba(74, 112, 166, .18) 1px, transparent 1px);
  background-size: 18px 18px;
  mask-image: linear-gradient(to bottom, #000 0%, transparent 88%);
}

.monitor-panel-hero__orb {
  position: absolute;
  border-radius: 999px;
  pointer-events: none;
  filter: blur(1px);
  opacity: .72;
}

.monitor-panel-hero__orb--one {
  width: 170px;
  height: 170px;
  right: 8%;
  top: -110px;
  border: 28px solid rgba(255, 255, 255, .62);
}

.monitor-panel-hero__orb--two {
  width: 90px;
  height: 90px;
  left: 38%;
  bottom: -58px;
  background: rgba(255, 255, 255, .48);
  box-shadow: 0 0 0 18px rgba(255, 255, 255, .22);
}

.monitor-panel-hero__content {
  padding: 34px 34px 30px;
}

.monitor-summary-card {
  padding: 18px 20px;
  border: 1px solid rgba(255, 255, 255, .92);
  border-radius: 24px;
  background: rgba(255, 255, 255, .68);
  backdrop-filter: blur(14px);
  box-shadow: 0 18px 46px rgba(86, 112, 153, .12), inset 0 1px 0 #fff;
}

.monitor-summary-card__avatar {
  position: relative;
  width: 68px;
  min-width: 68px;
  height: 68px;
  display: grid;
  place-items: center;
  border-radius: 22px;
  border: 1px solid color-mix(in srgb, var(--selected-accent) 26%, #fff 74%);
  background: linear-gradient(145deg, #fff 0%, var(--selected-soft) 100%);
  box-shadow: 0 18px 34px color-mix(in srgb, var(--selected-accent) 18%, transparent);
}

.monitor-summary-card__avatar-pulse {
  position: absolute;
  right: -4px;
  bottom: -4px;
  width: 17px;
  height: 17px;
  border: 4px solid #fff;
  border-radius: 999px;
  background: #34d399;
  box-shadow: 0 0 0 5px rgba(52, 211, 153, .18);
}

.monitor-summary-card__eyebrow {
  display: flex;
  align-items: center;
  gap: 6px;
  color: #7c3aed;
}

.monitor-summary-card__eyebrow ::v-deep .v-icon {
  color: #8b5cf6 !important;
}

.monitor-summary-card__title {
  font-size: clamp(1.55rem, 2.5vw, 2.15rem);
  color: #183153;
}

.monitor-summary-card__meta {
  color: #71829b;
}

.monitor-summary-card__clear {
  width: 46px !important;
  height: 46px !important;
  border: 1px solid rgba(251, 146, 60, .24) !important;
  background: linear-gradient(145deg, #fff 0%, #fff2e8 100%) !important;
  color: #ea580c !important;
  box-shadow: 0 12px 24px rgba(234, 88, 12, .12);
}

.monitor-live-pill {
  min-height: 44px;
  border-color: rgba(45, 212, 191, .3);
  background: linear-gradient(135deg, #ecfdf5 0%, #e0f2fe 100%);
  box-shadow: 0 12px 24px rgba(16, 185, 129, .12);
}

.monitor-overview-grid {
  margin-top: 20px;
}

.monitor-highlight-card {
  position: relative;
  overflow: hidden;
  min-height: 176px;
  padding: 20px;
  border: 1px solid rgba(255, 255, 255, .94);
  border-radius: 25px;
  box-shadow: 0 18px 40px rgba(80, 107, 147, .12), inset 0 1px 0 #fff;
}

.monitor-highlight-card::after {
  content: "";
  position: absolute;
  width: 110px;
  height: 110px;
  right: -48px;
  bottom: -54px;
  border-radius: 999px;
  background: rgba(255, 255, 255, .5);
  box-shadow: 0 0 0 18px rgba(255, 255, 255, .24);
}

.monitor-highlight-card--current {
  border-left: 0;
  background: linear-gradient(145deg, #eff8ff 0%, #eef2ff 100%);
}

.monitor-highlight-card--range {
  border-left: 0;
  background: linear-gradient(145deg, #fff8ed 0%, #fff1f2 100%);
}

.monitor-highlight-card--state {
  border-left: 0;
  background: linear-gradient(145deg, #ecfdf5 0%, #f0fdfa 100%);
}

.monitor-highlight-card--alert {
  background: linear-gradient(145deg, #fff1f2 0%, #fff7ed 100%);
}

.monitor-highlight-card__top {
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 17px;
}

.monitor-highlight-card__icon {
  width: 36px;
  height: 36px;
  display: grid;
  place-items: center;
  border-radius: 12px;
  background: rgba(255, 255, 255, .8);
  box-shadow: 0 8px 18px rgba(80, 107, 147, .1);
}

.monitor-highlight-card__icon ::v-deep .v-icon {
  color: #5676a6 !important;
  font-size: 20px !important;
}

.monitor-highlight-card__label {
  margin: 0;
  color: #6b7d98;
}

.monitor-highlight-card__value {
  position: relative;
  z-index: 1;
  font-family: "Segoe UI", sans-serif;
  font-size: clamp(2.5rem, 4vw, 3.5rem);
}

.monitor-highlight-card__value small {
  margin-left: 5px;
  font-size: .34em;
  color: #8191aa;
  letter-spacing: 0;
}

.monitor-highlight-card__subvalue {
  position: relative;
  z-index: 1;
  font-size: 1.2rem;
  color: #28415f;
}

.monitor-panel-body {
  padding: 24px 28px 30px !important;
  background:
    radial-gradient(circle at 95% 20%, rgba(186, 230, 253, .35), transparent 25%),
    linear-gradient(180deg, #fbfdff 0%, #f5f9ff 100%);
}

.monitor-controls-wrap {
  grid-template-columns: minmax(210px, auto) 1fr auto;
  margin: 0 0 18px;
  padding: 16px;
  border-color: rgba(185, 209, 239, .7);
  border-radius: 24px;
  background: rgba(255, 255, 255, .86);
  box-shadow: 0 16px 38px rgba(71, 105, 151, .09);
}

.monitor-controls-heading,
.monitor-chart-shell__heading {
  display: flex;
  align-items: center;
  gap: 12px;
}

.monitor-controls-heading__icon,
.monitor-chart-shell__icon {
  width: 44px;
  min-width: 44px;
  height: 44px;
  display: grid;
  place-items: center;
  border-radius: 14px;
  background: linear-gradient(145deg, #e0f2fe 0%, #ede9fe 100%);
  box-shadow: 0 10px 22px rgba(99, 102, 241, .12);
}

.monitor-controls-heading__icon ::v-deep .v-icon,
.monitor-chart-shell__icon ::v-deep .v-icon {
  color: #6366f1 !important;
}

.monitor-controls-help {
  margin-top: 3px;
  color: #91a0b4;
  font-size: 11px;
  font-weight: 600;
}

.monitor-controls-track {
  padding: 5px;
  border-radius: 18px;
  background: #f1f6fc;
}

.monitor-window-pill {
  min-height: 40px;
  background: transparent;
  color: #72839c;
}

.monitor-window-pill--active {
  background: linear-gradient(135deg, #60a5fa 0%, #8b5cf6 100%);
  box-shadow: 0 10px 22px rgba(99, 102, 241, .22);
}

.monitor-controls-trigger {
  min-width: 128px;
  min-height: 46px;
  color: #fff !important;
  box-shadow: 0 12px 26px rgba(16, 185, 129, .2);
}

.monitor-chart-shell {
  border-color: rgba(185, 209, 239, .72);
  border-radius: 28px;
  background: #fff;
  box-shadow: 0 22px 50px rgba(61, 96, 145, .11);
}

.monitor-chart-shell__header {
  padding: 20px 22px;
  border-bottom-color: rgba(214, 226, 241, .82);
  background: linear-gradient(135deg, #f3faff 0%, #faf7ff 58%, #fff9f2 100%);
}

.monitor-chart-shell__eyebrow {
  margin-bottom: 2px;
  color: #8b5cf6;
  font-size: 9px;
  font-weight: 900;
  letter-spacing: .18em;
}

.monitor-chart-shell__title {
  color: #243c5a;
  font-size: 1.12rem;
}

.monitor-chart-shell__body {
  background: linear-gradient(180deg, #fff 0%, #f8fbff 100%);
}

.monitor-stat-card {
  position: relative;
  overflow: hidden;
  min-height: 150px;
  border-color: rgba(255, 255, 255, .96) !important;
  border-radius: 23px !important;
  box-shadow: 0 16px 36px rgba(73, 102, 143, .1) !important;
  transition: transform .2s ease, box-shadow .2s ease;
}

.monitor-stat-card:hover {
  transform: translateY(-4px);
  box-shadow: 0 22px 44px rgba(73, 102, 143, .15) !important;
}

.monitor-stat-card__head {
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.monitor-stat-card__icon-wrap {
  width: 42px;
  min-width: 42px;
  height: 42px;
  border-radius: 14px;
}

.monitor-stat-card__spark {
  width: 32px;
  height: 14px;
  border-top: 3px solid rgba(96, 165, 250, .35);
  border-radius: 50%;
  transform: rotate(-8deg);
}

.monitor-stat-card__value {
  margin-top: 10px;
  font-family: "Segoe UI", sans-serif;
  font-size: 17px;
}

.monitor-info-dock {
  padding: 16px 18px 10px;
  border: 1px dashed rgba(153, 178, 211, .65);
  border-radius: 22px;
  background: rgba(255, 255, 255, .68);
}

.monitor-info-dock__title {
  display: flex;
  align-items: center;
  gap: 7px;
  margin-bottom: 12px;
  color: #627694;
  font-size: 11px;
  font-weight: 900;
  letter-spacing: .12em;
  text-transform: uppercase;
}

.monitor-info-dock__title ::v-deep .v-icon {
  color: #60a5fa !important;
}

.monitor-info-chip {
  min-height: 30px;
  color: #5f7391 !important;
  background: #fff !important;
  border-color: rgba(186, 206, 232, .72) !important;
}

@media (max-width: 960px) {
  .monitor-controls-wrap {
    grid-template-columns: 1fr;
  }

  .monitor-panel-hero__content,
  .monitor-panel-body {
    padding: 20px !important;
  }
}

/* Professional high-contrast monitor skin */
.monitor-panel-card {
  border: 1px solid #cbd7e4 !important;
  border-radius: 18px !important;
  background: #f3f6f9 !important;
  box-shadow: 0 18px 42px rgba(31, 55, 82, .14) !important;
}

.monitor-panel-hero {
  border-top: 5px solid #0088cc;
  border-bottom: 1px solid #d7e1ec;
  background: #fff !important;
}

.monitor-panel-hero__glow,
.monitor-panel-hero__orb {
  display: none !important;
}

.monitor-panel-hero__content {
  padding: 24px 26px 22px;
}

.monitor-summary-card {
  padding: 0;
  border: 0;
  border-radius: 0;
  background: transparent;
  backdrop-filter: none;
  box-shadow: none;
}

.monitor-summary-card__avatar {
  width: 62px;
  min-width: 62px;
  height: 62px;
  border: 1px solid #a9c6db;
  border-radius: 12px;
  background: #e8f5fb;
  box-shadow: none;
}

.monitor-summary-card__avatar-pulse {
  right: -5px;
  bottom: -5px;
  box-shadow: none;
}

.monitor-summary-card__eyebrow {
  color: #0077b6;
  letter-spacing: .14em;
}

.monitor-summary-card__eyebrow ::v-deep .v-icon {
  color: #0077b6 !important;
}

.monitor-summary-card__title {
  color: #172b3f;
  font-size: clamp(1.45rem, 2.2vw, 2rem);
}

.monitor-summary-card__meta {
  color: #61758a;
}

.monitor-live-pill {
  min-height: 40px;
  border: 1px solid #92d6bb;
  background: #e9f9f2;
  color: #087a54;
  box-shadow: none;
}

.monitor-summary-card__clear {
  border: 1px solid #f1b89e !important;
  border-radius: 10px !important;
  background: #fff4ee !important;
  box-shadow: none;
}

.monitor-overview-grid {
  margin-top: 22px;
}

.monitor-highlight-card {
  min-height: 148px;
  padding: 18px;
  border: 1px solid #cfdae5;
  border-radius: 12px;
  background: #fff !important;
  box-shadow: none;
}

.monitor-highlight-card::before {
  content: "";
  position: absolute;
  inset: 0 auto 0 0;
  width: 5px;
  background: #0088cc;
}

.monitor-highlight-card::after {
  display: none;
}

.monitor-highlight-card--range::before {
  background: #ef7d00;
}

.monitor-highlight-card--state::before {
  background: #0b9b69;
}

.monitor-highlight-card--alert::before {
  background: #d92d20;
}

.monitor-highlight-card__top {
  margin-bottom: 14px;
}

.monitor-highlight-card__icon {
  width: 34px;
  height: 34px;
  border: 1px solid #c9d7e4;
  border-radius: 8px;
  background: #f3f7fa;
  box-shadow: none;
}

.monitor-highlight-card__icon ::v-deep .v-icon {
  color: #31536f !important;
}

.monitor-highlight-card__label {
  color: #52677c;
}

.monitor-highlight-card__value {
  color: #0077b6 !important;
  font-family: "Segoe UI", sans-serif;
  font-size: clamp(2.3rem, 3.7vw, 3.25rem);
}

.monitor-highlight-card__subvalue {
  color: #172b3f;
}

.monitor-highlight-card__meta {
  color: #61758a;
}

.monitor-panel-body {
  padding: 22px 24px 26px !important;
  background: #f3f6f9;
}

.monitor-controls-wrap {
  grid-template-columns: minmax(210px, auto) 1fr auto;
  margin: 0 0 18px;
  padding: 14px;
  border: 1px solid #cbd7e4;
  border-radius: 12px;
  background: #fff;
  box-shadow: none;
}

.monitor-controls-heading__icon,
.monitor-chart-shell__icon {
  border: 1px solid #a9c6db;
  border-radius: 9px;
  background: #e8f5fb;
  box-shadow: none;
}

.monitor-controls-heading__icon ::v-deep .v-icon,
.monitor-chart-shell__icon ::v-deep .v-icon {
  color: #0077b6 !important;
}

.monitor-controls-help {
  color: #6f8295;
}

.monitor-controls-track {
  gap: 4px;
  padding: 4px;
  border: 1px solid #cfdae5;
  border-radius: 9px;
  background: #edf2f6;
}

.monitor-window-pill {
  min-height: 38px;
  border-radius: 6px;
  color: #52677c;
  background: transparent;
}

.monitor-window-pill:hover {
  color: #0077b6;
  background: #fff;
}

.monitor-window-pill--active {
  color: #fff;
  background: #0077b6;
  box-shadow: none;
}

.monitor-controls-trigger {
  min-width: 122px;
  min-height: 44px;
  border-radius: 8px !important;
  box-shadow: none;
}

.monitor-chart-shell {
  border: 1px solid #bdcbd9;
  border-radius: 12px;
  background: #fff;
  box-shadow: none;
}

.monitor-chart-shell__header {
  padding: 16px 18px;
  border-bottom: 1px solid #cbd7e4;
  background: #172b3f;
}

.monitor-chart-shell__eyebrow {
  color: #4fc3f7;
}

.monitor-chart-shell__title {
  color: #fff;
}

.monitor-chart-shell__subtitle {
  color: #b8c8d6;
}

.monitor-chart-shell__status {
  border-color: #486176;
  background: #243d53;
  color: #e4eef6;
}

.monitor-chart-shell__body {
  padding: 18px 16px 12px;
  background: #fff;
}

.monitor-chart-shell__empty {
  height: 390px;
}

.monitor-stat-card {
  min-height: 138px;
  border: 1px solid #cbd7e4 !important;
  border-radius: 10px !important;
  background: #fff !important;
  box-shadow: none !important;
}

.monitor-stat-card:hover {
  transform: translateY(-2px);
  border-color: #78b9dd !important;
  box-shadow: 0 10px 22px rgba(31, 55, 82, .1) !important;
}

.monitor-stat-card__icon-wrap {
  border: 1px solid #c4d3df;
  border-radius: 8px;
  box-shadow: none !important;
}

.monitor-stat-card__spark {
  border-top-color: #0088cc;
  opacity: .75;
}

.monitor-stat-card__label {
  color: #52677c;
}

.monitor-stat-card__value {
  color: #172b3f;
}

.monitor-info-dock {
  border: 1px solid #cbd7e4;
  border-radius: 10px;
  background: #fff;
}

.monitor-info-chip {
  color: #405a70 !important;
  background: #f4f7fa !important;
  border-color: #cbd7e4 !important;
}

@media (max-width: 960px) {
  .monitor-controls-wrap {
    grid-template-columns: 1fr;
  }
}
</style>
